import { gql, GraphQLClient } from "graphql-request";
import { createGraphqlClient, type GraphqlRequestConfig } from "./client";

// ========== GraphQL Queries ==========

const MY_TEAMS_QUERY = gql`
  query MyTeams {
    myTeams {
      id
      name
      slug
      iconUrl
      inviteCode
      plan
      maxMembers
      createdBy
      createdAt
      updatedAt
    }
  }
`;

const GET_TEAM_QUERY = gql`
  query GetTeam($slug: String!) {
    team(slug: $slug) {
      id
      name
      slug
      iconUrl
      inviteCode
      plan
      maxMembers
      createdBy
      createdAt
      updatedAt
    }
  }
`;

const GET_TEAM_MEMBERS_QUERY = gql`
  query GetTeamMembers($teamId: String!) {
    teamMembers(teamId: $teamId) {
      id
      teamId
      userId
      role
      trialExpiresAt
      crmUrl
      mattermostUsername
      mattermostUserId
      assignedMentorId
      memberStatus
      adminMemo
      mentoringChecks
      vocabChecks
      joinedAt
      createdAt
      user {
        id
        name
        email
      }
      isInternal
    }
  }
`;

const GET_LUAGATE_BOOTSTRAP_QUERY = gql`
  query GetLuagateBootstrap($slug: String!, $teamId: String!) {
    viewer {
      id
      name
      email
      role
    }
    team(slug: $slug) {
      id
      name
      slug
      iconUrl
      inviteCode
      plan
      maxMembers
      createdBy
      createdAt
      updatedAt
    }
    myTeamMember(teamId: $teamId) {
      id
      teamId
      userId
      role
      trialExpiresAt
      vocabChecks
      joinedAt
      createdAt
      accessRevoked
      user {
        id
        name
        email
      }
    }
    hasProdDev: myHasProdDev(teamId: $teamId)
  }
`;

const GET_TEAM_INVITATION_PREVIEW_QUERY = gql`
  query GetTeamInvitationPreview($token: String!) {
    teamInvitationPreview(token: $token) {
      teamName
      role
      expiresAt
      emailMatches
    }
  }
`;

const GET_TEAM_INVITATIONS_QUERY = gql`
  query GetTeamInvitations($teamId: String!) {
    teamInvitations(teamId: $teamId) {
      id
      teamId
      email
      token
      role
      expiresAt
      acceptedAt
      createdAt
    }
  }
`;

// ========== GraphQL Mutations ==========

const CREATE_TEAM_MUTATION = gql`
  mutation CreateTeam($input: CreateTeamInput!) {
    createTeam(input: $input) {
      id
      name
      slug
      iconUrl
      inviteCode
      plan
      maxMembers
      createdBy
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_TEAM_MUTATION = gql`
  mutation UpdateTeam($teamId: String!, $input: UpdateTeamInput!) {
    updateTeam(teamId: $teamId, input: $input) {
      id
      name
      slug
      iconUrl
      inviteCode
      plan
      maxMembers
      createdBy
      createdAt
      updatedAt
    }
  }
`;

const DELETE_TEAM_MUTATION = gql`
  mutation DeleteTeam($teamId: String!) {
    deleteTeam(teamId: $teamId)
  }
`;

const JOIN_TEAM_BY_CODE_MUTATION = gql`
  mutation JoinTeamByCode($inviteCode: String!) {
    joinTeamByCode(inviteCode: $inviteCode) {
      id
      teamId
      userId
      role
      joinedAt
      createdAt
      user {
        id
        name
        email
      }
    }
  }
`;

const INVITE_TO_TEAM_MUTATION = gql`
  mutation InviteToTeam($teamId: String!, $email: String!, $role: String) {
    inviteToTeam(teamId: $teamId, email: $email, role: $role) {
      id
      teamId
      email
      token
      role
      expiresAt
      acceptedAt
      createdAt
    }
  }
`;

const SEARCH_TEAM_USERS_QUERY = gql`
  query SearchTeamUsers($teamId: String!, $query: String!) {
    searchTeamUsers(teamId: $teamId, query: $query) {
      id
      name
      email
      isMember
    }
  }
`;

const ADD_EXISTING_USER_TO_TEAM_MUTATION = gql`
  mutation AddExistingUserToTeam($teamId: String!, $userId: String!) {
    addExistingUserToTeam(teamId: $teamId, userId: $userId) {
      id
      teamId
      userId
      role
      joinedAt
      createdAt
      user {
        id
        name
        email
      }
    }
  }
`;

const ACCEPT_TEAM_INVITATION_MUTATION = gql`
  mutation AcceptTeamInvitation($token: String!) {
    acceptTeamInvitation(token: $token) {
      id
      teamId
      userId
      role
      joinedAt
      createdAt
      user {
        id
        name
        email
      }
    }
  }
`;

const UPDATE_TEAM_MEMBER_ROLE_MUTATION = gql`
  mutation UpdateTeamMemberRole(
    $teamId: String!
    $userId: String!
    $role: String!
  ) {
    updateTeamMemberRole(teamId: $teamId, userId: $userId, role: $role) {
      id
      teamId
      userId
      role
      joinedAt
      createdAt
      user {
        id
        name
        email
      }
    }
  }
`;

const REMOVE_TEAM_MEMBER_MUTATION = gql`
  mutation RemoveTeamMember($teamId: String!, $userId: String!) {
    removeTeamMember(teamId: $teamId, userId: $userId)
  }
`;

const REGENERATE_TEAM_INVITE_CODE_MUTATION = gql`
  mutation RegenerateTeamInviteCode($teamId: String!) {
    regenerateTeamInviteCode(teamId: $teamId) {
      id
      name
      slug
      iconUrl
      inviteCode
      plan
      maxMembers
      createdBy
      createdAt
      updatedAt
    }
  }
`;

// ========== Types ==========

export interface Team {
  id: string;
  name: string;
  slug: string;
  iconUrl?: string | null;
  inviteCode: string;
  plan: "free" | "pro" | "enterprise";
  maxMembers?: number | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface TeamMemberUser {
  id: string;
  name: string;
  email: string;
}

export interface TeamMember {
  id: string;
  teamId: string;
  userId: string;
  role: "owner" | "admin" | "instructor" | "mentor" | "member" | "trial";
  trialExpiresAt?: string | null;
  crmUrl?: string | null;
  mattermostUsername?: string | null;
  mattermostUserId?: string | null;
  assignedMentorId?: string | null;
  memberStatus?: string | null;
  adminMemo?: string | null;
  mentoringChecks?: boolean[] | null;
  vocabChecks?: string[] | null;
  joinedAt: string;
  createdAt: string;
  user: TeamMemberUser;
  /** 受講の権利の取り消しの印があるか。LuaGate の bootstrap だけが引く */
  accessRevoked?: boolean;
  /** 社内の検証用アカウント (user.is_internal)。teamMembers だけが引く。受講生向けの名簿には出てこない */
  isInternal?: boolean;
}

export interface LuagateViewer {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface LuagateBootstrap {
  viewer: LuagateViewer | null;
  team: Team | null;
  member: TeamMember | null;
  /** 本人に実プロダクト開発演習が付いているか (管理画面のプラン管理で付けた分)。体験・退会・行が無い人は false */
  hasProdDev: boolean;
  // LuaGate pages use membership/access data only. Pricing pages use the
  // separate MyPlanInfo operation from the non-LuaGate root path.
  planInfo: null;
}

export interface TeamUserSearchResult {
  id: string;
  name: string;
  email: string;
  isMember: boolean;
}

export interface TeamInvitation {
  id: string;
  teamId: string;
  email: string;
  token: string;
  role: "admin" | "member";
  expiresAt: string;
  acceptedAt?: string | null;
  createdAt: string;
}

/** 参加の確認の画面に出す分。招待のメールアドレスと token は含まない */
export interface TeamInvitationPreview {
  teamName: string;
  role: string;
  expiresAt: string;
  /** 見ている人のメールアドレスが招待の宛先と同じか。違うと承諾は断られる */
  emailMatches: boolean;
}

export interface CreateTeamInput {
  name: string;
  slug: string;
  iconUrl?: string;
  plan?: string;
  maxMembers?: number;
}

export interface UpdateTeamInput {
  name?: string;
  iconUrl?: string;
  plan?: string;
  maxMembers?: number;
}

export interface GraphqlTeamOptions extends GraphqlRequestConfig {
  client?: GraphQLClient;
}

function ensureClient(options?: GraphqlTeamOptions) {
  if (options?.client) {
    return options.client;
  }
  return createGraphqlClient(options);
}

// ========== API Functions ==========

/**
 * 現在のユーザーが所属するチーム一覧を取得
 */
export async function getMyTeams(
  options?: GraphqlTeamOptions,
): Promise<Team[]> {
  const client = ensureClient(options);
  const data = await client.request(MY_TEAMS_QUERY);
  return (data as any).myTeams ?? [];
}

/**
 * slugでチームを取得
 */
export async function getTeamBySlug(
  slug: string,
  options?: GraphqlTeamOptions,
): Promise<Team | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_QUERY, { slug });
  return (data as any).team ?? null;
}

/**
 * チームメンバー一覧を取得
 */
export async function getTeamMembers(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamMember[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_MEMBERS_QUERY, { teamId });
  return (data as any).teamMembers ?? [];
}

/**
 * LuaGateの初期表示に必要な本人・所属だけを1往復で取得する。
 * 全メンバー一覧は管理画面以外では取得しない。
 */
export async function getLuagateBootstrap(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<LuagateBootstrap> {
  const client = ensureClient(options);
  const data = await client.request<{
    viewer: LuagateViewer | null;
    team: Team | null;
    myTeamMember: TeamMember | null;
    hasProdDev?: boolean | null;
  }>(GET_LUAGATE_BOOTSTRAP_QUERY, { slug: "luagate", teamId });

  return {
    viewer: data.viewer ?? null,
    team: data.team ?? null,
    member: data.myTeamMember ?? null,
    hasProdDev: data.hasProdDev === true,
    planInfo: null,
  };
}

/**
 * チーム招待一覧を取得（管理者のみ）
 */
export async function getTeamInvitations(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamInvitation[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_INVITATIONS_QUERY, { teamId });
  return (data as any).teamInvitations ?? [];
}

/**
 * チームを作成
 */
export async function createTeam(
  input: CreateTeamInput,
  options?: GraphqlTeamOptions,
): Promise<Team> {
  const client = ensureClient(options);
  const data = await client.request(CREATE_TEAM_MUTATION, { input });
  return (data as any).createTeam;
}

/**
 * チームを更新
 */
export async function updateTeam(
  teamId: string,
  input: UpdateTeamInput,
  options?: GraphqlTeamOptions,
): Promise<Team> {
  const client = ensureClient(options);
  const data = await client.request(UPDATE_TEAM_MUTATION, { teamId, input });
  return (data as any).updateTeam;
}

/**
 * チームを削除
 */
export async function deleteTeam(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_TEAM_MUTATION, { teamId });
  return (data as any).deleteTeam;
}

/**
 * 招待コードでチームに参加
 */
export async function joinTeamByCode(
  inviteCode: string,
  options?: GraphqlTeamOptions,
): Promise<TeamMember> {
  const client = ensureClient(options);
  const data = await client.request(JOIN_TEAM_BY_CODE_MUTATION, { inviteCode });
  return (data as any).joinTeamByCode;
}

/**
 * メールでチームに招待
 */
export async function inviteToTeam(
  teamId: string,
  email: string,
  role?: "admin" | "member",
  options?: GraphqlTeamOptions,
): Promise<TeamInvitation> {
  const client = ensureClient(options);
  const data = await client.request(INVITE_TO_TEAM_MUTATION, {
    teamId,
    email,
    role,
  });
  return (data as any).inviteToTeam;
}

/**
 * 名前・メールで既存ユーザーを検索（管理者のみ）。isMember は当該チームの所属有無
 */
export async function searchTeamUsers(
  teamId: string,
  query: string,
  options?: GraphqlTeamOptions,
): Promise<TeamUserSearchResult[]> {
  const client = ensureClient(options);
  const data = await client.request(SEARCH_TEAM_USERS_QUERY, { teamId, query });
  return (data as any).searchTeamUsers ?? [];
}

/**
 * 既存ユーザーを member としてチームに直接追加（管理者のみ）
 */
export async function addExistingUserToTeam(
  teamId: string,
  userId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamMember> {
  const client = ensureClient(options);
  const data = await client.request(ADD_EXISTING_USER_TO_TEAM_MUTATION, {
    teamId,
    userId,
  });
  return (data as any).addExistingUserToTeam;
}

/**
 * 招待のリンクを開いた人に、参加の確認の画面で見せる分を取る。何も書き換えない。
 * 使えない招待 (無効、承諾済み、期限切れ) は例外になる。
 */
export async function getTeamInvitationPreview(
  token: string,
  options?: GraphqlTeamOptions,
): Promise<TeamInvitationPreview> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_INVITATION_PREVIEW_QUERY, { token });
  return (data as any).teamInvitationPreview;
}

/**
 * 招待を承諾してチームに参加
 */
export async function acceptTeamInvitation(
  token: string,
  options?: GraphqlTeamOptions,
): Promise<TeamMember> {
  const client = ensureClient(options);
  const data = await client.request(ACCEPT_TEAM_INVITATION_MUTATION, { token });
  return (data as any).acceptTeamInvitation;
}

/**
 * メンバーの権限を変更
 */
export async function updateTeamMemberRole(
  teamId: string,
  userId: string,
  role: "admin" | "instructor" | "mentor" | "member" | "trial",
  options?: GraphqlTeamOptions,
): Promise<TeamMember> {
  const client = ensureClient(options);
  const data = await client.request(UPDATE_TEAM_MEMBER_ROLE_MUTATION, {
    teamId,
    userId,
    role,
  });
  return (data as any).updateTeamMemberRole;
}

// ===== 1on1メンタリング管理 =====

const UPDATE_MEMBER_MENTORING_MUTATION = gql`
  mutation UpdateTeamMemberMentoring(
    $teamId: String!
    $userId: String!
    $crmUrl: String
    $mattermostUsername: String
    $assignedMentorId: String
    $memberStatus: String
    $adminMemo: String
    $mentoringChecks: [Boolean!]
    $joinedAt: String
  ) {
    updateTeamMemberMentoring(
      teamId: $teamId
      userId: $userId
      crmUrl: $crmUrl
      mattermostUsername: $mattermostUsername
      assignedMentorId: $assignedMentorId
      memberStatus: $memberStatus
      adminMemo: $adminMemo
      mentoringChecks: $mentoringChecks
      joinedAt: $joinedAt
    ) {
      id
      crmUrl
      mattermostUsername
      assignedMentorId
      memberStatus
      adminMemo
      mentoringChecks
      joinedAt
    }
  }
`;

export async function updateTeamMemberMentoring(
  teamId: string,
  userId: string,
  data: {
    crmUrl?: string;
    mattermostUsername?: string;
    assignedMentorId?: string;
    memberStatus?: string;
    adminMemo?: string;
    mentoringChecks?: boolean[];
    joinedAt?: string;
  },
  options?: GraphqlTeamOptions,
): Promise<TeamMember> {
  const client = ensureClient(options);
  const result = await client.request(UPDATE_MEMBER_MENTORING_MUTATION, {
    teamId,
    userId,
    ...data,
  });
  return (result as any).updateTeamMemberMentoring;
}

// ===== Mattermost user_id 紐付け =====

export interface MattermostUserResolved {
  id: string;
  username: string;
  nickname: string | null;
}

const RESOLVE_MATTERMOST_USER_MUTATION = gql`
  mutation ResolveMattermostUserByUsername($username: String!) {
    resolveMattermostUserByUsername(username: $username) {
      id
      username
      nickname
    }
  }
`;

/**
 * Mattermost username を user_id に解決する。
 * 見つからない場合は null。
 */
export async function resolveMattermostUserByUsername(
  username: string,
  options?: GraphqlTeamOptions,
): Promise<MattermostUserResolved | null> {
  const client = ensureClient(options);
  const data = await client.request(RESOLVE_MATTERMOST_USER_MUTATION, {
    username,
  });
  return (data as any).resolveMattermostUserByUsername ?? null;
}

const SET_TEAM_MEMBER_MATTERMOST_ID_MUTATION = gql`
  mutation SetTeamMemberMattermostId(
    $memberId: String!
    $mattermostUserId: String
    $mattermostUsername: String
  ) {
    setTeamMemberMattermostId(
      memberId: $memberId
      mattermostUserId: $mattermostUserId
      mattermostUsername: $mattermostUsername
    )
  }
`;

/**
 * チームメンバーに Mattermost user_id (+ username) を紐付ける。
 * null を渡すと解除（クリア）される。
 */
export async function setTeamMemberMattermostId(
  memberId: string,
  mattermostUserId: string | null,
  mattermostUsername: string | null,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(SET_TEAM_MEMBER_MATTERMOST_ID_MUTATION, {
    memberId,
    mattermostUserId,
    mattermostUsername,
  });
  return (data as any).setTeamMemberMattermostId === true;
}

// ===== IT単語チェック =====

const UPDATE_MY_VOCAB_CHECKS_MUTATION = gql`
  mutation UpdateMyVocabChecks($teamId: String!, $vocabChecks: [String!]!) {
    updateMyVocabChecks(teamId: $teamId, vocabChecks: $vocabChecks) {
      id
      vocabChecks
    }
  }
`;

export async function updateMyVocabChecks(
  teamId: string,
  vocabChecks: string[],
  options?: GraphqlTeamOptions,
): Promise<TeamMember> {
  const client = ensureClient(options);
  const result = await client.request(UPDATE_MY_VOCAB_CHECKS_MUTATION, {
    teamId,
    vocabChecks,
  });
  return (result as any).updateMyVocabChecks;
}

/**
 * メンバーを削除
 */
export async function removeTeamMember(
  teamId: string,
  userId: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(REMOVE_TEAM_MEMBER_MUTATION, {
    teamId,
    userId,
  });
  return (data as any).removeTeamMember;
}

/**
 * 招待コードを再生成
 */
export async function regenerateTeamInviteCode(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<Team> {
  const client = ensureClient(options);
  const data = await client.request(REGENERATE_TEAM_INVITE_CODE_MUTATION, {
    teamId,
  });
  return (data as any).regenerateTeamInviteCode;
}

// ========== Team Lectures ==========

const GET_TEAM_LECTURES_QUERY = gql`
  query GetTeamLectures($teamId: String!) {
    teamLectures(teamId: $teamId) {
      id
      teamId
      number
      title
      description
      scheduledAt
      durationMinutes
      zoomUrl
      resources {
        title
        url
        type
      }
      surveyUrl
      recordingUrl
      recommendedProgress
      targetAudience
      seriesTitle
      seriesOrder
      isRequired
      instructor {
        id
        name
        type
        title
        bio
        photoUrl
      }
      createdAt
      updatedAt
    }
  }
`;

export interface TeamLectureResource {
  title: string;
  url: string;
  type: string;
}

export interface TeamInstructor {
  id: string;
  name: string;
  type: string;
  title?: string | null;
  bio?: string | null;
  photoUrl?: string | null;
}

export interface TeamLecture {
  id: string;
  teamId: string;
  number: string;
  title: string;
  description?: string | null;
  scheduledAt: string;
  durationMinutes: number;
  zoomUrl?: string | null;
  resources?: TeamLectureResource[] | null;
  surveyUrl?: string | null;
  recordingUrl?: string | null;
  recommendedProgress?: number | null;
  targetAudience?: string | null;
  /** シリーズ名と何回目か。授業アーカイブで前後の回を辿る単位 */
  seriesTitle?: string | null;
  seriesOrder?: number | null;
  isRequired: boolean;
  instructor?: TeamInstructor | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * チームの講義一覧を取得
 */
export async function getTeamLectures(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamLecture[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_LECTURES_QUERY, { teamId });
  return (data as any).teamLectures ?? [];
}

// Team Instructors Query
const GET_TEAM_INSTRUCTORS_QUERY = gql`
  query GetTeamInstructors($teamId: String!) {
    teamInstructors(teamId: $teamId) {
      id
      name
      type
      title
      bio
      photoUrl
    }
  }
`;

/**
 * チームの講師一覧を取得
 */
export async function getTeamInstructors(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamInstructor[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_INSTRUCTORS_QUERY, { teamId });
  return (data as any).teamInstructors ?? [];
}

// Team Lecture Mutations
const CREATE_TEAM_LECTURE_MUTATION = gql`
  mutation CreateTeamLecture(
    $teamId: String!
    $number: String!
    $title: String!
    $description: String
    $scheduledAt: String!
    $durationMinutes: Int
    $zoomUrl: String
    $surveyUrl: String
    $recordingUrl: String
    $recommendedProgress: Int
    $targetAudience: String
    $isRequired: Boolean
    $instructorId: String
    $seriesTitle: String
    $seriesOrder: Int
  ) {
    createTeamLecture(
      teamId: $teamId
      number: $number
      title: $title
      description: $description
      scheduledAt: $scheduledAt
      durationMinutes: $durationMinutes
      zoomUrl: $zoomUrl
      surveyUrl: $surveyUrl
      recordingUrl: $recordingUrl
      recommendedProgress: $recommendedProgress
      targetAudience: $targetAudience
      isRequired: $isRequired
      instructorId: $instructorId
      seriesTitle: $seriesTitle
      seriesOrder: $seriesOrder
    ) {
      id
      teamId
      number
      title
      description
      scheduledAt
      durationMinutes
      zoomUrl
      surveyUrl
      recordingUrl
      recommendedProgress
      targetAudience
      seriesTitle
      seriesOrder
      isRequired
      instructor {
        id
        name
        type
        title
      }
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_TEAM_LECTURE_MUTATION = gql`
  mutation UpdateTeamLecture(
    $lectureId: String!
    $number: String
    $title: String
    $description: String
    $scheduledAt: String
    $durationMinutes: Int
    $zoomUrl: String
    $surveyUrl: String
    $recordingUrl: String
    $recommendedProgress: Int
    $targetAudience: String
    $isRequired: Boolean
    $instructorId: String
    $seriesTitle: String
    $seriesOrder: Int
  ) {
    updateTeamLecture(
      lectureId: $lectureId
      number: $number
      title: $title
      description: $description
      scheduledAt: $scheduledAt
      durationMinutes: $durationMinutes
      zoomUrl: $zoomUrl
      surveyUrl: $surveyUrl
      recordingUrl: $recordingUrl
      recommendedProgress: $recommendedProgress
      targetAudience: $targetAudience
      isRequired: $isRequired
      instructorId: $instructorId
      seriesTitle: $seriesTitle
      seriesOrder: $seriesOrder
    ) {
      id
      teamId
      number
      title
      description
      scheduledAt
      durationMinutes
      zoomUrl
      surveyUrl
      recordingUrl
      recommendedProgress
      targetAudience
      seriesTitle
      seriesOrder
      isRequired
      instructor {
        id
        name
        type
        title
      }
      createdAt
      updatedAt
    }
  }
`;

const DELETE_TEAM_LECTURE_MUTATION = gql`
  mutation DeleteTeamLecture($lectureId: String!) {
    deleteTeamLecture(lectureId: $lectureId)
  }
`;

export interface CreateTeamLectureInput {
  teamId: string;
  number: string;
  title: string;
  description?: string;
  scheduledAt: string;
  durationMinutes?: number;
  zoomUrl?: string;
  surveyUrl?: string;
  recordingUrl?: string;
  recommendedProgress?: number | null;
  targetAudience?: string | null;
  isRequired?: boolean;
  instructorId?: string | null;
  seriesTitle?: string | null;
  seriesOrder?: number | null;
}

export interface UpdateTeamLectureInput {
  number?: string;
  title?: string;
  description?: string;
  scheduledAt?: string;
  durationMinutes?: number;
  zoomUrl?: string;
  surveyUrl?: string;
  recordingUrl?: string;
  recommendedProgress?: number | null;
  targetAudience?: string | null;
  isRequired?: boolean;
  instructorId?: string | null;
  seriesTitle?: string | null;
  seriesOrder?: number | null;
}

/**
 * チームの講義を作成（管理者のみ）
 */
export async function createTeamLecture(
  input: CreateTeamLectureInput,
  options?: GraphqlTeamOptions,
): Promise<TeamLecture> {
  const client = ensureClient(options);
  const data = await client.request(CREATE_TEAM_LECTURE_MUTATION, input);
  return (data as any).createTeamLecture;
}

/**
 * チームの講義を更新（管理者のみ）
 */
export async function updateTeamLecture(
  lectureId: string,
  input: UpdateTeamLectureInput,
  options?: GraphqlTeamOptions,
): Promise<TeamLecture> {
  const client = ensureClient(options);
  const data = await client.request(UPDATE_TEAM_LECTURE_MUTATION, {
    lectureId,
    ...input,
  });
  return (data as any).updateTeamLecture;
}

/**
 * チームの講義を削除（管理者のみ）
 */
export async function deleteTeamLecture(
  lectureId: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_TEAM_LECTURE_MUTATION, {
    lectureId,
  });
  return (data as any).deleteTeamLecture;
}

// ========== Team Assignments (Homework) ==========

const GET_TEAM_ASSIGNMENTS_QUERY = gql`
  query GetTeamAssignments($teamId: String!) {
    teamAssignments(teamId: $teamId) {
      id
      teamId
      title
      description
      instructions
      type
      isRequired
      displayOrder
      deadline
      maxPoints
      relativeMonth
      relativeDay
      figmaUrl
      loomUrl
      tag
      createdBy
      createdAt
      updatedAt
      submissions {
        id
        assignmentId
        userId
        submissionNumber
        content
        attachmentUrl
        submittedAt
        grade
        feedback
        gradedAt
        gradedBy
        status
        createdAt
        updatedAt
      }
      comments {
        id
        assignmentId
        authorId
        authorName
        content
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_TEAM_ASSIGNMENT_DETAIL_QUERY = gql`
  query GetTeamAssignmentDetail($teamId: String!, $displayOrder: Int!) {
    teamAssignmentDetail(teamId: $teamId, displayOrder: $displayOrder) {
      assignment {
        id
        teamId
        title
        description
        instructions
        type
        isRequired
        displayOrder
        deadline
        maxPoints
        relativeMonth
        relativeDay
        figmaUrl
        loomUrl
        tag
        createdBy
        createdAt
        updatedAt
        submissions {
          id
          assignmentId
          userId
          submissionNumber
          content
          attachmentUrl
          submittedAt
          grade
          feedback
          gradedAt
          gradedBy
          status
          createdAt
          updatedAt
        }
        comments {
          id
          assignmentId
          authorId
          authorName
          content
          createdAt
          updatedAt
        }
      }
      previous {
        id
        title
        displayOrder
      }
      next {
        id
        title
        displayOrder
      }
    }
  }
`;

const GET_ASSIGNMENT_SUBMISSIONS_QUERY = gql`
  query GetAssignmentSubmissions($assignmentId: String!) {
    teamAssignmentSubmissions(assignmentId: $assignmentId) {
      id
      assignmentId
      userId
      submissionNumber
      content
      attachmentUrl
      submittedAt
      grade
      feedback
      gradedAt
      gradedBy
      status
      createdAt
      updatedAt
    }
  }
`;

export interface TeamAssignment {
  id: string;
  teamId: string;
  title: string;
  description?: string | null;
  instructions?: string | null;
  type: string;
  isRequired: boolean;
  displayOrder: number;
  deadline?: string | null;
  maxPoints?: number | null;
  relativeMonth?: number | null; // 入学後何ヶ月目か
  relativeDay?: number | null; // その月の何日が締切か
  figmaUrl?: string | null; // Figmaデザインへのリンク
  loomUrl?: string | null; // Loom動画へのリンク
  tag?: string | null; // タグ（実践プロジェクト等）
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  submissions: TeamAssignmentSubmission[];
  comments: TeamAssignmentComment[];
}

export interface TeamAssignmentNavigation {
  id: string;
  title: string;
  displayOrder: number;
}

export interface TeamAssignmentDetail {
  assignment: TeamAssignment;
  previous?: TeamAssignmentNavigation | null;
  next?: TeamAssignmentNavigation | null;
}

export interface TeamAssignmentSubmission {
  id: string;
  assignmentId: string;
  userId: string;
  submissionNumber: number;
  content: string;
  attachmentUrl?: string | null;
  submittedAt: string;
  grade?: number | null;
  feedback?: string | null;
  gradedAt?: string | null;
  gradedBy?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * チームの課題一覧を取得
 */
export async function getTeamAssignments(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamAssignment[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_ASSIGNMENTS_QUERY, { teamId });
  return (data as any).teamAssignments ?? [];
}

/**
 * 表示順で指定した課題1件と前後リンクを取得する。
 * 提出とコメントは対象課題だけに限定される。
 */
export async function getTeamAssignmentDetail(
  teamId: string,
  displayOrder: number,
  options?: GraphqlTeamOptions,
): Promise<TeamAssignmentDetail | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_ASSIGNMENT_DETAIL_QUERY, {
    teamId,
    displayOrder,
  });
  return (data as any).teamAssignmentDetail ?? null;
}

/**
 * 課題の提出一覧を取得
 */
export async function getAssignmentSubmissions(
  assignmentId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamAssignmentSubmission[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_ASSIGNMENT_SUBMISSIONS_QUERY, {
    assignmentId,
  });
  return (data as any).teamAssignmentSubmissions ?? [];
}

// ========== Team Projects ==========

const GET_TEAM_PROJECTS_QUERY = gql`
  query GetTeamProjects($teamId: String!) {
    teamProjects(teamId: $teamId) {
      id
      teamId
      title
      description
      goal
      status
      maxParticipants
      startDate
      endDate
      githubUrl
      techStack
      difficulty
      iconUrl
      slackChannelId
      createdAt
      updatedAt
      participants {
        id
        projectId
        userId
        role
        contributionNote
        joinedAt
        user {
          id
          name
          email
        }
      }
    }
  }
`;

const GET_TEAM_PROJECT_DASHBOARD_ITEMS_QUERY = gql`
  query GetTeamProjectDashboardItems($teamId: String!) {
    teamProjects(teamId: $teamId) {
      id
      status
      title
      startDate
      endDate
    }
  }
`;

const GET_PROJECT_PARTICIPANTS_QUERY = gql`
  query GetProjectParticipants($projectId: String!) {
    teamProjectParticipants(projectId: $projectId) {
      id
      projectId
      userId
      role
      contributionNote
      joinedAt
      user {
        id
        name
        email
      }
    }
  }
`;

export interface TeamProject {
  id: string;
  teamId: string;
  title: string;
  description?: string | null;
  goal?: string | null;
  status: string;
  maxParticipants?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  githubUrl?: string | null;
  techStack?: string[] | null;
  difficulty: string;
  iconUrl?: string | null;
  slackChannelId?: string | null;
  createdAt: string;
  updatedAt: string;
  participants: TeamProjectParticipant[];
}

export interface TeamProjectDashboardItem {
  id: string;
  status: string;
  /** ダッシュボードの「次のチーム開発」に名前と始まる日を出すために読む */
  title?: string | null;
  startDate?: string | null;
  endDate?: string | null;
}

export interface TeamProjectParticipantUser {
  id: string;
  name: string;
  email: string;
}

export interface TeamProjectParticipant {
  id: string;
  projectId: string;
  userId: string;
  role?: string | null;
  contributionNote?: string | null;
  joinedAt: string;
  user: TeamProjectParticipantUser;
}

/**
 * チームのプロジェクト一覧を取得
 */
export async function getTeamProjects(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamProject[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_PROJECTS_QUERY, { teamId });
  return (data as any).teamProjects ?? [];
}

/**
 * ダッシュボードの完了件数計算に必要な id/status だけを取得
 */
export async function getTeamProjectDashboardItems(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamProjectDashboardItem[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_PROJECT_DASHBOARD_ITEMS_QUERY, {
    teamId,
  });
  return (data as any).teamProjects ?? [];
}

/**
 * プロジェクトの参加者一覧を取得
 */
export async function getProjectParticipants(
  projectId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamProjectParticipant[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_PROJECT_PARTICIPANTS_QUERY, {
    projectId,
  });
  return (data as any).teamProjectParticipants ?? [];
}

// ========== Team Project Mutations (管理者用) ==========

const CREATE_TEAM_PROJECT_MUTATION = gql`
  mutation CreateTeamProject(
    $teamId: String!
    $title: String!
    $description: String
    $goal: String
    $status: String
    $maxParticipants: Int
    $startDate: String
    $endDate: String
    $githubUrl: String
    $techStack: String
    $difficulty: String
    $iconUrl: String
    $slackChannelId: String
  ) {
    createTeamProject(
      teamId: $teamId
      title: $title
      description: $description
      goal: $goal
      status: $status
      maxParticipants: $maxParticipants
      startDate: $startDate
      endDate: $endDate
      githubUrl: $githubUrl
      techStack: $techStack
      difficulty: $difficulty
      iconUrl: $iconUrl
      slackChannelId: $slackChannelId
    ) {
      id
      teamId
      title
      description
      goal
      status
      maxParticipants
      startDate
      endDate
      githubUrl
      techStack
      difficulty
      iconUrl
      slackChannelId
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_TEAM_PROJECT_MUTATION = gql`
  mutation UpdateTeamProject(
    $projectId: String!
    $title: String
    $description: String
    $goal: String
    $status: String
    $maxParticipants: Int
    $startDate: String
    $endDate: String
    $githubUrl: String
    $techStack: String
    $difficulty: String
    $iconUrl: String
    $slackChannelId: String
  ) {
    updateTeamProject(
      projectId: $projectId
      title: $title
      description: $description
      goal: $goal
      status: $status
      maxParticipants: $maxParticipants
      startDate: $startDate
      endDate: $endDate
      githubUrl: $githubUrl
      techStack: $techStack
      difficulty: $difficulty
      iconUrl: $iconUrl
      slackChannelId: $slackChannelId
    ) {
      id
      teamId
      title
      description
      goal
      status
      maxParticipants
      startDate
      endDate
      githubUrl
      techStack
      difficulty
      iconUrl
      slackChannelId
      createdAt
      updatedAt
    }
  }
`;

const DELETE_TEAM_PROJECT_MUTATION = gql`
  mutation DeleteTeamProject($projectId: String!) {
    deleteTeamProject(projectId: $projectId)
  }
`;

export interface CreateTeamProjectInput {
  teamId: string;
  title: string;
  description?: string;
  goal?: string;
  status?: string;
  maxParticipants?: number;
  startDate?: string;
  endDate?: string;
  githubUrl?: string;
  techStack?: string | string[];
  difficulty?: string;
  iconUrl?: string;
  slackChannelId?: string;
}

export interface UpdateTeamProjectInput {
  title?: string;
  description?: string;
  goal?: string;
  status?: string;
  maxParticipants?: number;
  startDate?: string;
  endDate?: string;
  githubUrl?: string;
  techStack?: string | string[];
  difficulty?: string;
  iconUrl?: string;
  slackChannelId?: string;
}

/** The API stores this varchar as a JSON array and parses it on reads. */
export function serializeTeamProjectTechStack(
  techStack: string | string[] | undefined,
): string | undefined {
  return Array.isArray(techStack) ? JSON.stringify(techStack) : techStack;
}

/**
 * チームのプロジェクトを作成（管理者のみ）
 */
export async function createTeamProject(
  input: CreateTeamProjectInput,
  options?: GraphqlTeamOptions,
): Promise<TeamProject> {
  const client = ensureClient(options);
  const data = await client.request(CREATE_TEAM_PROJECT_MUTATION, {
    ...input,
    techStack: serializeTeamProjectTechStack(input.techStack),
  });
  return (data as any).createTeamProject;
}

/**
 * チームのプロジェクトを更新（管理者のみ）
 */
export async function updateTeamProject(
  projectId: string,
  input: UpdateTeamProjectInput,
  options?: GraphqlTeamOptions,
): Promise<TeamProject> {
  const client = ensureClient(options);
  const data = await client.request(UPDATE_TEAM_PROJECT_MUTATION, {
    projectId,
    ...input,
    techStack: serializeTeamProjectTechStack(input.techStack),
  });
  return (data as any).updateTeamProject;
}

/**
 * チームのプロジェクトを削除（管理者のみ）
 */
export async function deleteTeamProject(
  projectId: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_TEAM_PROJECT_MUTATION, {
    projectId,
  });
  return (data as any).deleteTeamProject;
}

// ========== Team Mentoring ==========

const GET_TEAM_MENTORING_SLOTS_QUERY = gql`
  query GetTeamMentoringSlots($teamId: String!) {
    teamMentoringSlots(teamId: $teamId) {
      id
      teamId
      mentorId
      slotDate
      startTime
      endTime
      durationMinutes
      zoomUrl
      isAvailable
      createdAt
      mentor {
        id
        name
        email
      }
    }
  }
`;

const GET_MY_MENTORING_BOOKINGS_QUERY = gql`
  query GetMyMentoringBookings($teamId: String!) {
    myMentoringBookings(teamId: $teamId) {
      id
      slotId
      userId
      status
      note
      slackNotifiedAt
      cancelledAt
      createdAt
      updatedAt
      user {
        id
        name
        email
      }
      slot {
        id
        teamId
        mentorId
        slotDate
        startTime
        endTime
        durationMinutes
        zoomUrl
        isAvailable
        createdAt
        mentor {
          id
          name
          email
        }
      }
    }
  }
`;

export interface TeamMentoringSlotMentor {
  id: string;
  name: string;
  email: string;
}

export interface TeamMentoringSlot {
  id: string;
  teamId: string;
  mentorId: string;
  slotDate: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  zoomUrl?: string | null;
  isAvailable: boolean;
  createdAt: string;
  mentor: TeamMentoringSlotMentor;
}

export interface TeamMentoringBookingUser {
  id: string;
  name: string;
  email: string;
}

export interface TeamMentoringBooking {
  id: string;
  slotId: string;
  userId: string;
  status: string;
  note?: string | null;
  slackNotifiedAt?: string | null;
  cancelledAt?: string | null;
  createdAt: string;
  updatedAt: string;
  user: TeamMentoringBookingUser;
  slot: TeamMentoringSlot;
}

/**
 * チームのメンタリング枠一覧を取得
 */
export async function getTeamMentoringSlots(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamMentoringSlot[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_MENTORING_SLOTS_QUERY, { teamId });
  return (data as any).teamMentoringSlots ?? [];
}

/**
 * 自分のメンタリング予約一覧を取得
 */
export async function getMyMentoringBookings(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamMentoringBooking[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_MY_MENTORING_BOOKINGS_QUERY, {
    teamId,
  });
  return (data as any).myMentoringBookings ?? [];
}

// ========== Team Dashboard Stats ==========

const GET_MY_TEAM_DASHBOARD_STATS_QUERY = gql`
  query GetMyTeamDashboardStats($teamId: String!) {
    myTeamDashboardStats(teamId: $teamId) {
      totalStudyMinutes
      completedLessons
      mentoringSessionsCount
      projectsParticipating
      assignmentsSubmitted
      assignmentsGraded
    }
  }
`;

export interface TeamDashboardStats {
  totalStudyMinutes: number;
  completedLessons: number;
  mentoringSessionsCount: number;
  projectsParticipating: number;
  assignmentsSubmitted: number;
  assignmentsGraded: number;
}

/**
 * 自分のチームダッシュボード統計を取得
 */
export async function getMyTeamDashboardStats(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamDashboardStats> {
  const client = ensureClient(options);
  const data = await client.request(GET_MY_TEAM_DASHBOARD_STATS_QUERY, {
    teamId,
  });
  return (
    (data as any).myTeamDashboardStats ?? {
      totalStudyMinutes: 0,
      completedLessons: 0,
      mentoringSessionsCount: 0,
      projectsParticipating: 0,
      assignmentsSubmitted: 0,
      assignmentsGraded: 0,
    }
  );
}

// ========== Activity Heatmap ==========

const GET_MY_TEAM_ACTIVITY_HEATMAP_QUERY = gql`
  query GetMyTeamActivityHeatmap($teamId: String!, $year: Int!) {
    myTeamActivityHeatmap(teamId: $teamId, year: $year) {
      date
      count
    }
  }
`;

export interface DailyActivity {
  date: string;
  count: number;
}

/**
 * 自分のアクティビティヒートマップデータを取得
 */
export async function getMyTeamActivityHeatmap(
  teamId: string,
  year: number,
  options?: GraphqlTeamOptions,
): Promise<DailyActivity[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_MY_TEAM_ACTIVITY_HEATMAP_QUERY, {
    teamId,
    year,
  });
  return (data as any).myTeamActivityHeatmap ?? [];
}

// ========== Bounded Team Dashboard Overview ==========

export interface TeamDashboardOverview {
  stats: TeamDashboardStats;
  activityHeatmap: DailyActivity[];
  comprehensionSummary: {
    completed: number;
    total: number;
  };
}

export const GET_TEAM_DASHBOARD_OVERVIEW_QUERY = gql`
  query TeamDashboardOverview($teamId: String!, $year: Int!) {
    teamDashboardOverview(teamId: $teamId, year: $year) {
      stats {
        totalStudyMinutes
        completedLessons
        mentoringSessionsCount
        projectsParticipating
        assignmentsSubmitted
        assignmentsGraded
      }
      activityHeatmap {
        date
        count
      }
      comprehensionSummary {
        completed
        total
      }
    }
  }
`;

const EMPTY_TEAM_DASHBOARD_OVERVIEW: TeamDashboardOverview = {
  stats: {
    totalStudyMinutes: 0,
    completedLessons: 0,
    mentoringSessionsCount: 0,
    projectsParticipating: 0,
    assignmentsSubmitted: 0,
    assignmentsGraded: 0,
  },
  activityHeatmap: [],
  comprehensionSummary: { completed: 0, total: 0 },
};

/**
 * Dashboard stats, annual activity, and comprehension counts in one request.
 */
export async function getTeamDashboardOverview(
  teamId: string,
  year: number,
  options?: GraphqlTeamOptions,
): Promise<TeamDashboardOverview> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_DASHBOARD_OVERVIEW_QUERY, {
    teamId,
    year,
  });
  return (
    (data as any).teamDashboardOverview ?? {
      ...EMPTY_TEAM_DASHBOARD_OVERVIEW,
      stats: { ...EMPTY_TEAM_DASHBOARD_OVERVIEW.stats },
      activityHeatmap: [],
      comprehensionSummary: {
        ...EMPTY_TEAM_DASHBOARD_OVERVIEW.comprehensionSummary,
      },
    }
  );
}

// ========== Team Exclusive Courses ==========

const GET_TEAM_EXCLUSIVE_COURSES_QUERY = gql`
  query GetTeamExclusiveCourses($teamId: String!) {
    teamExclusiveCourses(teamId: $teamId) {
      id
      teamId
      title
      description
      thumbnailUrl
      accessType
      displayOrder
      isPublished
      createdBy
      createdAt
      updatedAt
    }
  }
`;

const GET_TEAM_EXCLUSIVE_COURSE_DETAIL_QUERY = gql`
  query GetTeamExclusiveCourseDetail($courseId: String!) {
    teamExclusiveCourseDetail(courseId: $courseId) {
      course {
        id
        teamId
        title
        description
        thumbnailUrl
        accessType
        displayOrder
        isPublished
        createdBy
        createdAt
        updatedAt
      }
      sections {
        id
        courseId
        title
        description
        displayOrder
        createdAt
        updatedAt
        lessons {
          id
          sectionId
          title
          description
          contentType
          contentUrl
          contentText
          durationMinutes
          displayOrder
          isFreePreview
          createdAt
          updatedAt
        }
      }
    }
  }
`;

const GET_TEAM_EXCLUSIVE_COURSE_PROGRESS_QUERY = gql`
  query GetTeamExclusiveCourseProgress($courseId: String!) {
    teamExclusiveCourseProgress(courseId: $courseId) {
      id
      lessonId
      userId
      isCompleted
      completedAt
      lastPositionSeconds
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_TEAM_EXCLUSIVE_LESSON_PROGRESS_MUTATION = gql`
  mutation UpdateTeamExclusiveLessonProgress(
    $lessonId: String!
    $isCompleted: Boolean!
    $lastPositionSeconds: Int
  ) {
    updateTeamExclusiveLessonProgress(
      lessonId: $lessonId
      isCompleted: $isCompleted
      lastPositionSeconds: $lastPositionSeconds
    ) {
      id
      lessonId
      userId
      isCompleted
      completedAt
      lastPositionSeconds
      createdAt
      updatedAt
    }
  }
`;

export interface TeamExclusiveCourse {
  id: string;
  teamId: string;
  title: string;
  description?: string | null;
  thumbnailUrl?: string | null;
  accessType: string;
  displayOrder: number;
  isPublished: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface TeamExclusiveCourseLesson {
  id: string;
  sectionId: string;
  title: string;
  description?: string | null;
  contentType: string;
  contentUrl?: string | null;
  contentText?: string | null;
  durationMinutes?: number | null;
  displayOrder: number;
  isFreePreview: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TeamExclusiveCourseSection {
  id: string;
  courseId: string;
  title: string;
  description?: string | null;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  lessons: TeamExclusiveCourseLesson[];
}

export interface TeamExclusiveCourseDetail {
  course: TeamExclusiveCourse;
  sections: TeamExclusiveCourseSection[];
}

export interface TeamExclusiveCourseProgress {
  id: string;
  lessonId: string;
  userId: string;
  isCompleted: boolean;
  completedAt?: string | null;
  lastPositionSeconds: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * チームの限定コース一覧を取得
 */
export async function getTeamExclusiveCourses(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamExclusiveCourse[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_EXCLUSIVE_COURSES_QUERY, {
    teamId,
  });
  return (data as any).teamExclusiveCourses ?? [];
}

/**
 * 限定コースの詳細を取得
 */
export async function getTeamExclusiveCourseDetail(
  courseId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamExclusiveCourseDetail | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_EXCLUSIVE_COURSE_DETAIL_QUERY, {
    courseId,
  });
  return (data as any).teamExclusiveCourseDetail ?? null;
}

/**
 * 限定コースの進捗を取得
 */
export async function getTeamExclusiveCourseProgress(
  courseId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamExclusiveCourseProgress[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_EXCLUSIVE_COURSE_PROGRESS_QUERY, {
    courseId,
  });
  return (data as any).teamExclusiveCourseProgress ?? [];
}

/**
 * 限定コースのレッスン進捗を更新
 */
export async function updateTeamExclusiveLessonProgress(
  lessonId: string,
  isCompleted: boolean,
  lastPositionSeconds?: number,
  options?: GraphqlTeamOptions,
): Promise<TeamExclusiveCourseProgress> {
  const client = ensureClient(options);
  const data = await client.request(
    UPDATE_TEAM_EXCLUSIVE_LESSON_PROGRESS_MUTATION,
    {
      lessonId,
      isCompleted,
      lastPositionSeconds,
    },
  );
  return (data as any).updateTeamExclusiveLessonProgress;
}

// ========== Team Jobs ==========

const GET_TEAM_JOBS_QUERY = gql`
  query GetTeamJobs($teamId: String!) {
    teamJobs(teamId: $teamId) {
      id
      teamId
      title
      description
      requiredSkills
      reward
      workType
      status
      deadline
      createdAt
      updatedAt
    }
  }
`;

const GET_MY_JOB_APPLICATIONS_QUERY = gql`
  query GetMyJobApplications($teamId: String!) {
    myJobApplications(teamId: $teamId) {
      id
      jobId
      userId
      message
      status
      slackNotifiedAt
      createdAt
      updatedAt
      job {
        id
        teamId
        title
        description
        requiredSkills
        reward
        workType
        status
        deadline
        createdAt
        updatedAt
      }
    }
  }
`;

const APPLY_TO_JOB_MUTATION = gql`
  mutation ApplyToJob($jobId: String!, $message: String) {
    applyToJob(jobId: $jobId, message: $message) {
      id
      jobId
      userId
      message
      status
      createdAt
      updatedAt
    }
  }
`;

export interface TeamJob {
  id: string;
  teamId: string;
  title: string;
  description?: string | null;
  requiredSkills?: string | null;
  reward?: string | null;
  workType: string;
  status: string;
  deadline?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TeamJobApplication {
  id: string;
  jobId: string;
  userId: string;
  message?: string | null;
  status: string;
  slackNotifiedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  job: TeamJob;
}

/**
 * チームの案件一覧を取得
 */
export async function getTeamJobs(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamJob[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_JOBS_QUERY, { teamId });
  return (data as any).teamJobs ?? [];
}

/**
 * 自分の応募一覧を取得
 */
export async function getMyJobApplications(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamJobApplication[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_MY_JOB_APPLICATIONS_QUERY, { teamId });
  return (data as any).myJobApplications ?? [];
}

/**
 * 案件に応募する
 */
export async function applyToJob(
  jobId: string,
  message?: string,
  options?: GraphqlTeamOptions,
): Promise<TeamJobApplication> {
  const client = ensureClient(options);
  const data = await client.request(APPLY_TO_JOB_MUTATION, { jobId, message });
  return (data as any).applyToJob;
}

// ========== Team Job Admin Mutations ==========

const CREATE_TEAM_JOB_MUTATION = gql`
  mutation CreateTeamJob(
    $teamId: String!
    $title: String!
    $description: String
    $requiredSkills: String
    $reward: String
    $workType: String
    $status: String
    $deadline: String
  ) {
    createTeamJob(
      teamId: $teamId
      title: $title
      description: $description
      requiredSkills: $requiredSkills
      reward: $reward
      workType: $workType
      status: $status
      deadline: $deadline
    ) {
      id
      teamId
      title
      description
      requiredSkills
      reward
      workType
      status
      deadline
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_TEAM_JOB_MUTATION = gql`
  mutation UpdateTeamJob(
    $jobId: String!
    $title: String
    $description: String
    $requiredSkills: String
    $reward: String
    $workType: String
    $status: String
    $deadline: String
  ) {
    updateTeamJob(
      jobId: $jobId
      title: $title
      description: $description
      requiredSkills: $requiredSkills
      reward: $reward
      workType: $workType
      status: $status
      deadline: $deadline
    ) {
      id
      teamId
      title
      description
      requiredSkills
      reward
      workType
      status
      deadline
      createdAt
      updatedAt
    }
  }
`;

const DELETE_TEAM_JOB_MUTATION = gql`
  mutation DeleteTeamJob($jobId: String!) {
    deleteTeamJob(jobId: $jobId)
  }
`;

export interface CreateTeamJobInput {
  teamId: string;
  title: string;
  description?: string;
  requiredSkills?: string;
  reward?: string;
  workType?: string;
  status?: string;
  deadline?: string;
}

export interface UpdateTeamJobInput {
  title?: string;
  description?: string;
  requiredSkills?: string;
  reward?: string;
  workType?: string;
  status?: string;
  deadline?: string;
}

/**
 * チームの案件を作成（管理者のみ）
 */
export async function createTeamJob(
  input: CreateTeamJobInput,
  options?: GraphqlTeamOptions,
): Promise<TeamJob> {
  const client = ensureClient(options);
  const data = await client.request(CREATE_TEAM_JOB_MUTATION, input);
  return (data as any).createTeamJob;
}

/**
 * チームの案件を更新（管理者のみ）
 */
export async function updateTeamJob(
  jobId: string,
  input: UpdateTeamJobInput,
  options?: GraphqlTeamOptions,
): Promise<TeamJob> {
  const client = ensureClient(options);
  const data = await client.request(UPDATE_TEAM_JOB_MUTATION, {
    jobId,
    ...input,
  });
  return (data as any).updateTeamJob;
}

/**
 * チームの案件を削除（管理者のみ）
 */
export async function deleteTeamJob(
  jobId: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_TEAM_JOB_MUTATION, { jobId });
  return (data as any).deleteTeamJob;
}

// ========== Mentoring Mutations ==========

const BOOK_MENTORING_SLOT_MUTATION = gql`
  mutation BookMentoringSlot($slotId: String!, $note: String) {
    bookMentoringSlot(slotId: $slotId, note: $note) {
      id
      slotId
      userId
      status
      note
      createdAt
      updatedAt
    }
  }
`;

const CANCEL_MENTORING_BOOKING_MUTATION = gql`
  mutation CancelMentoringBooking($bookingId: String!) {
    cancelMentoringBooking(bookingId: $bookingId) {
      id
      slotId
      userId
      status
      note
      cancelledAt
      createdAt
      updatedAt
    }
  }
`;

export interface SimpleMentoringBooking {
  id: string;
  slotId: string;
  userId: string;
  status: string;
  note?: string | null;
  slackNotifiedAt?: string | null;
  cancelledAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * メンタリング枠を予約
 */
export async function bookMentoringSlot(
  slotId: string,
  note?: string,
  options?: GraphqlTeamOptions,
): Promise<SimpleMentoringBooking> {
  const client = ensureClient(options);
  const data = await client.request(BOOK_MENTORING_SLOT_MUTATION, {
    slotId,
    note,
  });
  return (data as any).bookMentoringSlot;
}

/**
 * メンタリング予約をキャンセル
 */
export async function cancelMentoringBooking(
  bookingId: string,
  options?: GraphqlTeamOptions,
): Promise<SimpleMentoringBooking> {
  const client = ensureClient(options);
  const data = await client.request(CANCEL_MENTORING_BOOKING_MUTATION, {
    bookingId,
  });
  return (data as any).cancelMentoringBooking;
}

// Mentoring Slot Admin Mutations
const CREATE_MENTORING_SLOT_MUTATION = gql`
  mutation CreateMentoringSlot(
    $teamId: String!
    $mentorId: String!
    $slotDate: String!
    $startTime: String!
    $endTime: String!
    $durationMinutes: Int
    $zoomUrl: String
  ) {
    createMentoringSlot(
      teamId: $teamId
      mentorId: $mentorId
      slotDate: $slotDate
      startTime: $startTime
      endTime: $endTime
      durationMinutes: $durationMinutes
      zoomUrl: $zoomUrl
    ) {
      id
      teamId
      mentorId
      slotDate
      startTime
      endTime
      durationMinutes
      zoomUrl
      isAvailable
      createdAt
    }
  }
`;

const UPDATE_MENTORING_SLOT_MUTATION = gql`
  mutation UpdateMentoringSlot(
    $slotId: String!
    $slotDate: String
    $startTime: String
    $endTime: String
    $durationMinutes: Int
    $zoomUrl: String
    $isAvailable: Boolean
  ) {
    updateMentoringSlot(
      slotId: $slotId
      slotDate: $slotDate
      startTime: $startTime
      endTime: $endTime
      durationMinutes: $durationMinutes
      zoomUrl: $zoomUrl
      isAvailable: $isAvailable
    ) {
      id
      teamId
      mentorId
      slotDate
      startTime
      endTime
      durationMinutes
      zoomUrl
      isAvailable
      createdAt
    }
  }
`;

const DELETE_MENTORING_SLOT_MUTATION = gql`
  mutation DeleteMentoringSlot($slotId: String!) {
    deleteMentoringSlot(slotId: $slotId)
  }
`;

export interface SimpleMentoringSlot {
  id: string;
  teamId: string;
  mentorId: string;
  slotDate: string;
  startTime: string;
  endTime: string;
  durationMinutes: number;
  zoomUrl?: string | null;
  isAvailable: boolean;
  createdAt: string;
}

export interface CreateMentoringSlotInput {
  teamId: string;
  mentorId: string;
  slotDate: string;
  startTime: string;
  endTime: string;
  durationMinutes?: number;
  zoomUrl?: string;
}

export interface UpdateMentoringSlotInput {
  slotDate?: string;
  startTime?: string;
  endTime?: string;
  durationMinutes?: number;
  zoomUrl?: string;
  isAvailable?: boolean;
}

/**
 * メンタリング枠を作成（管理者のみ）
 */
export async function createMentoringSlot(
  input: CreateMentoringSlotInput,
  options?: GraphqlTeamOptions,
): Promise<SimpleMentoringSlot> {
  const client = ensureClient(options);
  const data = await client.request(CREATE_MENTORING_SLOT_MUTATION, input);
  return (data as any).createMentoringSlot;
}

/**
 * メンタリング枠を更新（管理者のみ）
 */
export async function updateMentoringSlot(
  slotId: string,
  input: UpdateMentoringSlotInput,
  options?: GraphqlTeamOptions,
): Promise<SimpleMentoringSlot> {
  const client = ensureClient(options);
  const data = await client.request(UPDATE_MENTORING_SLOT_MUTATION, {
    slotId,
    ...input,
  });
  return (data as any).updateMentoringSlot;
}

/**
 * メンタリング枠を削除（管理者のみ）
 */
export async function deleteMentoringSlot(
  slotId: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_MENTORING_SLOT_MUTATION, { slotId });
  return (data as any).deleteMentoringSlot;
}

// ========== Assignment Mutations ==========

const SUBMIT_ASSIGNMENT_MUTATION = gql`
  mutation SubmitAssignment(
    $assignmentId: String!
    $content: String!
    $attachmentUrl: String
  ) {
    submitAssignment(
      assignmentId: $assignmentId
      content: $content
      attachmentUrl: $attachmentUrl
    ) {
      id
      assignmentId
      userId
      submissionNumber
      content
      attachmentUrl
      submittedAt
      grade
      feedback
      gradedAt
      gradedBy
      status
      createdAt
      updatedAt
    }
  }
`;

/**
 * 宿題を提出
 */
export async function submitAssignment(
  assignmentId: string,
  content: string,
  attachmentUrl?: string,
  options?: GraphqlTeamOptions,
): Promise<TeamAssignmentSubmission> {
  const client = ensureClient(options);
  const data = await client.request(SUBMIT_ASSIGNMENT_MUTATION, {
    assignmentId,
    content,
    attachmentUrl,
  });
  return (data as any).submitAssignment;
}

// ========== Assignment Comments ==========

const GET_ASSIGNMENT_COMMENTS_QUERY = gql`
  query GetAssignmentComments($assignmentId: String!) {
    teamAssignmentComments(assignmentId: $assignmentId) {
      id
      assignmentId
      authorId
      authorName
      content
      createdAt
      updatedAt
    }
  }
`;

const ADD_ASSIGNMENT_COMMENT_MUTATION = gql`
  mutation AddAssignmentComment(
    $assignmentId: String!
    $content: String!
    $authorName: String!
  ) {
    addAssignmentComment(
      assignmentId: $assignmentId
      content: $content
      authorName: $authorName
    ) {
      id
      assignmentId
      authorId
      authorName
      content
      createdAt
      updatedAt
    }
  }
`;

export interface TeamAssignmentComment {
  id: string;
  assignmentId: string;
  authorId: string;
  authorName: string;
  content: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * 課題のコメント一覧を取得
 */
export async function getAssignmentComments(
  assignmentId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamAssignmentComment[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_ASSIGNMENT_COMMENTS_QUERY, {
    assignmentId,
  });
  return (data as any).teamAssignmentComments ?? [];
}

/**
 * 課題にコメントを追加（管理者のみ）
 */
export async function addAssignmentComment(
  assignmentId: string,
  content: string,
  authorName: string,
  options?: GraphqlTeamOptions,
): Promise<TeamAssignmentComment> {
  const client = ensureClient(options);
  const data = await client.request(ADD_ASSIGNMENT_COMMENT_MUTATION, {
    assignmentId,
    content,
    authorName,
  });
  return (data as any).addAssignmentComment;
}

// ========== Project Mutations ==========

const JOIN_PROJECT_MUTATION = gql`
  mutation JoinProject($projectId: String!, $role: String) {
    joinProject(projectId: $projectId, role: $role) {
      id
      projectId
      userId
      role
      contributionNote
      joinedAt
    }
  }
`;

export interface SimpleProjectParticipant {
  id: string;
  projectId: string;
  userId: string;
  role?: string | null;
  contributionNote?: string | null;
  joinedAt: string;
}

/**
 * プロジェクトに参加
 */
export async function joinProject(
  projectId: string,
  role?: string,
  options?: GraphqlTeamOptions,
): Promise<SimpleProjectParticipant> {
  const client = ensureClient(options);
  const data = await client.request(JOIN_PROJECT_MUTATION, { projectId, role });
  return (data as any).joinProject;
}

// ========== Lecture Survey ==========

const SUBMIT_LECTURE_SURVEY_MUTATION = gql`
  mutation SubmitLectureSurvey(
    $lectureId: String!
    $overallRating: Int!
    $contentRating: Int!
    $paceRating: Int!
    $clarity: String!
    $helpfulTopics: [String!]!
    $difficultTopics: String
    $suggestions: String
    $additionalComments: String
  ) {
    submitLectureSurvey(
      lectureId: $lectureId
      overallRating: $overallRating
      contentRating: $contentRating
      paceRating: $paceRating
      clarity: $clarity
      helpfulTopics: $helpfulTopics
      difficultTopics: $difficultTopics
      suggestions: $suggestions
      additionalComments: $additionalComments
    ) {
      id
      lectureId
      userId
      overallRating
      contentRating
      paceRating
      clarity
      helpfulTopics
      difficultTopics
      suggestions
      additionalComments
      createdAt
    }
  }
`;

export interface TeamLectureSurvey {
  id: string;
  lectureId: string;
  userId: string;
  overallRating: number;
  contentRating: number;
  paceRating: number;
  clarity: string;
  helpfulTopics: string[];
  difficultTopics?: string | null;
  suggestions?: string | null;
  additionalComments?: string | null;
  createdAt: string;
}

export interface SubmitLectureSurveyInput {
  overallRating: number;
  contentRating: number;
  paceRating: number;
  clarity: string;
  helpfulTopics: string[];
  difficultTopics?: string;
  suggestions?: string;
  additionalComments?: string;
}

/**
 * 講義アンケートを提出
 */
export async function submitLectureSurvey(
  lectureId: string,
  data: SubmitLectureSurveyInput,
  options?: GraphqlTeamOptions,
): Promise<TeamLectureSurvey> {
  const client = ensureClient(options);
  const result = await client.request(SUBMIT_LECTURE_SURVEY_MUTATION, {
    lectureId,
    ...data,
  });
  return (result as any).submitLectureSurvey;
}

// ========== Team Resources (資料保管庫) ==========

const GET_TEAM_RESOURCES_QUERY = gql`
  query GetTeamResources($teamId: String!, $category: String) {
    teamResources(teamId: $teamId, category: $category) {
      id
      teamId
      lectureId
      title
      description
      type
      url
      category
      sortOrder
      isFeatured
      createdAt
      updatedAt
    }
  }
`;

export interface TeamResource {
  id: string;
  teamId: string;
  lectureId?: string | null;
  title: string;
  description?: string | null;
  type: "slides" | "document" | "video" | "link" | "template";
  url: string;
  category?: string | null;
  sortOrder: number;
  isFeatured: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * チームのリソース（資料）一覧を取得
 */
export async function getTeamResources(
  teamId: string,
  category?: string,
  options?: GraphqlTeamOptions,
): Promise<TeamResource[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_RESOURCES_QUERY, {
    teamId,
    category,
  });
  return (data as any).teamResources ?? [];
}

const GET_LECTURE_RESOURCES_QUERY = gql`
  query GetLectureResources($lectureId: String!) {
    lectureResources(lectureId: $lectureId) {
      id
      teamId
      lectureId
      title
      description
      type
      url
      category
      sortOrder
      isFeatured
      createdAt
      updatedAt
    }
  }
`;

/**
 * 授業に紐づく資料一覧を取得
 */
export async function getLectureResources(
  lectureId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamResource[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_LECTURE_RESOURCES_QUERY, { lectureId });
  return (data as any).lectureResources ?? [];
}

// ========== Team Resource Mutations (管理者用) ==========

const CREATE_TEAM_RESOURCE_MUTATION = gql`
  mutation CreateTeamResource(
    $teamId: String!
    $title: String!
    $url: String!
    $type: String!
    $description: String
    $category: String
    $lectureId: String
    $sortOrder: Int
    $isFeatured: Boolean
  ) {
    createTeamResource(
      teamId: $teamId
      title: $title
      url: $url
      type: $type
      description: $description
      category: $category
      lectureId: $lectureId
      sortOrder: $sortOrder
      isFeatured: $isFeatured
    ) {
      id
      teamId
      lectureId
      title
      description
      type
      url
      category
      sortOrder
      isFeatured
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_TEAM_RESOURCE_MUTATION = gql`
  mutation UpdateTeamResource(
    $resourceId: String!
    $title: String
    $url: String
    $type: String
    $description: String
    $category: String
    $lectureId: String
    $sortOrder: Int
    $isFeatured: Boolean
  ) {
    updateTeamResource(
      resourceId: $resourceId
      title: $title
      url: $url
      type: $type
      description: $description
      category: $category
      lectureId: $lectureId
      sortOrder: $sortOrder
      isFeatured: $isFeatured
    ) {
      id
      teamId
      lectureId
      title
      description
      type
      url
      category
      sortOrder
      isFeatured
      createdAt
      updatedAt
    }
  }
`;

const DELETE_TEAM_RESOURCE_MUTATION = gql`
  mutation DeleteTeamResource($resourceId: String!) {
    deleteTeamResource(resourceId: $resourceId)
  }
`;

export interface CreateTeamResourceInput {
  teamId: string;
  title: string;
  url: string;
  type: string;
  description?: string;
  category?: string;
  lectureId?: string;
  sortOrder?: number;
  isFeatured?: boolean;
}

export interface UpdateTeamResourceInput {
  title?: string;
  url?: string;
  type?: string;
  description?: string;
  category?: string;
  lectureId?: string;
  sortOrder?: number;
  isFeatured?: boolean;
}

/**
 * チームのリソースを作成（管理者のみ）
 */
export async function createTeamResource(
  input: CreateTeamResourceInput,
  options?: GraphqlTeamOptions,
): Promise<TeamResource> {
  const client = ensureClient(options);
  const data = await client.request(CREATE_TEAM_RESOURCE_MUTATION, input);
  return (data as any).createTeamResource;
}

/**
 * チームのリソースを更新（管理者のみ）
 */
export async function updateTeamResource(
  resourceId: string,
  input: UpdateTeamResourceInput,
  options?: GraphqlTeamOptions,
): Promise<TeamResource> {
  const client = ensureClient(options);
  const data = await client.request(UPDATE_TEAM_RESOURCE_MUTATION, {
    resourceId,
    ...input,
  });
  return (data as any).updateTeamResource;
}

/**
 * チームのリソースを削除（管理者のみ）
 */
export async function deleteTeamResource(
  resourceId: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_TEAM_RESOURCE_MUTATION, {
    resourceId,
  });
  return (data as any).deleteTeamResource;
}

// ========== Team Articles (限定コラム) ==========

const GET_TEAM_ARTICLES_QUERY = gql`
  query GetTeamArticles($teamId: String!, $category: String) {
    teamArticles(teamId: $teamId, category: $category) {
      id
      teamId
      slug
      title
      excerpt
      content
      coverImageUrl
      authorName
      authorAvatarUrl
      instructorId
      instructor {
        id
        name
        title
        photoUrl
      }
      category
      tags
      isPublished
      publishedAt
      viewCount
      createdAt
      updatedAt
    }
  }
`;

const GET_TEAM_ARTICLE_QUERY = gql`
  query GetTeamArticle($teamId: String!, $slug: String!) {
    teamArticle(teamId: $teamId, slug: $slug) {
      id
      teamId
      slug
      title
      excerpt
      content
      coverImageUrl
      authorName
      authorAvatarUrl
      instructorId
      instructor {
        id
        name
        title
        photoUrl
      }
      category
      tags
      isPublished
      publishedAt
      viewCount
      createdAt
      updatedAt
    }
  }
`;

/**
 * Related-card projection. The server applies a hard cap to `limit`; keeping
 * this query metadata-only prevents article MEDIUMTEXT bodies from crossing
 * either the DB or GraphQL boundary.
 */
export const GET_RELATED_TEAM_ARTICLES_QUERY = gql`
  query GetRelatedTeamArticles(
    $teamId: String!
    $excludeSlug: String!
    $limit: Int!
  ) {
    teamArticles(teamId: $teamId, excludeSlug: $excludeSlug, limit: $limit) {
      id
      slug
      title
      coverImageUrl
      category
      tags
    }
  }
`;

/** 記事の書いた人として見せる講師 (bio は出さない) */
export interface TeamArticleInstructor {
  id: string;
  name: string;
  title?: string | null;
  photoUrl?: string | null;
}

export interface TeamArticle {
  id: string;
  teamId: string;
  slug: string;
  title: string;
  excerpt?: string | null;
  content?: string | null;
  coverImageUrl?: string | null;
  authorName?: string | null;
  authorAvatarUrl?: string | null;
  /** 書いた人の講師。あるときは名前・写真・肩書きを instructor から出す */
  instructorId?: string | null;
  instructor?: TeamArticleInstructor | null;
  category?: string | null;
  tags?: string[] | null;
  isPublished: boolean;
  publishedAt?: string | null;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
}

export type RelatedTeamArticle = Pick<
  TeamArticle,
  "id" | "slug" | "title" | "coverImageUrl" | "category" | "tags"
>;

export const RELATED_TEAM_CONTENT_LIMIT = 7;

/**
 * チームの記事一覧を取得
 */
export async function getTeamArticles(
  teamId: string,
  category?: string,
  options?: GraphqlTeamOptions,
): Promise<TeamArticle[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_ARTICLES_QUERY, {
    teamId,
    category,
  });
  return (data as any).teamArticles ?? [];
}

/**
 * チームの記事詳細を取得（閲覧数を増やす）
 */
export async function getTeamArticle(
  teamId: string,
  slug: string,
  options?: GraphqlTeamOptions,
): Promise<TeamArticle | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_ARTICLE_QUERY, { teamId, slug });
  return (data as any).teamArticle ?? null;
}

/**
 * Fetch the bounded metadata candidates used by the detail-page related rail.
 * The page still slices to six cards so the UI contract is independent of an
 * API returning fewer rows.
 */
export async function getRelatedTeamArticles(
  teamId: string,
  excludeSlug: string,
  options?: GraphqlTeamOptions,
): Promise<RelatedTeamArticle[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_RELATED_TEAM_ARTICLES_QUERY, {
    teamId,
    excludeSlug,
    limit: RELATED_TEAM_CONTENT_LIMIT,
  });
  return (data as any).teamArticles ?? [];
}

// ========== Create Team Article ==========

const CREATE_TEAM_ARTICLE_MUTATION = gql`
  mutation CreateTeamArticle($input: CreateTeamArticleInput!) {
    createTeamArticle(input: $input) {
      id
      teamId
      slug
      title
      excerpt
      content
      coverImageUrl
      authorName
      authorAvatarUrl
      instructorId
      instructor {
        id
        name
        title
        photoUrl
      }
      category
      tags
      isPublished
      publishedAt
      viewCount
      createdAt
      updatedAt
    }
  }
`;

export interface CreateTeamArticleInput {
  teamId: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  coverImageUrl?: string;
  authorName?: string;
  authorAvatarUrl?: string;
  /** 同じチームの有効な講師の id。省略なら講師なし */
  instructorId?: string;
  category?: string;
  tags?: string[];
  isPublished?: boolean;
}

/**
 * チームの記事を作成（管理者のみ）
 */
export async function createTeamArticle(
  input: CreateTeamArticleInput,
  options?: GraphqlTeamOptions,
): Promise<TeamArticle> {
  const client = ensureClient(options);
  const data = await client.request(CREATE_TEAM_ARTICLE_MUTATION, { input });
  return (data as any).createTeamArticle;
}

// ========== Update Team Article ==========

const UPDATE_TEAM_ARTICLE_MUTATION = gql`
  mutation UpdateTeamArticle(
    $articleId: String!
    $input: UpdateTeamArticleInput!
  ) {
    updateTeamArticle(articleId: $articleId, input: $input) {
      id
      teamId
      slug
      title
      excerpt
      content
      coverImageUrl
      authorName
      authorAvatarUrl
      instructorId
      instructor {
        id
        name
        title
        photoUrl
      }
      category
      tags
      isPublished
      publishedAt
      viewCount
      createdAt
      updatedAt
    }
  }
`;

export interface UpdateTeamArticleInput {
  title?: string;
  slug?: string;
  excerpt?: string;
  content?: string;
  category?: string;
  tags?: string[];
  isPublished?: boolean;
  /** 省略なら変えない。空文字なら講師の結びつけを外す */
  instructorId?: string;
}

/**
 * チームの記事を更新（管理者のみ）
 */
export async function updateTeamArticle(
  articleId: string,
  input: UpdateTeamArticleInput,
  options?: GraphqlTeamOptions,
): Promise<TeamArticle> {
  const client = ensureClient(options);
  const data = await client.request(UPDATE_TEAM_ARTICLE_MUTATION, {
    articleId,
    input,
  });
  return (data as any).updateTeamArticle;
}

// ========== Portal Textbooks (テキスト教材) ==========

const GET_PORTAL_TEXTBOOKS_QUERY = gql`
  query GetPortalTextbooks($teamId: String!, $category: String) {
    portalTextbooks(teamId: $teamId, category: $category) {
      id
      teamId
      slug
      title
      excerpt
      coverImageUrl
      authorName
      authorAvatarUrl
      category
      tags
      isBeta
      isPublished
      displayOrder
      publishedAt
      viewCount
      createdAt
      updatedAt
      bodyChars
    }
  }
`;

const GET_PORTAL_TEXTBOOK_QUERY = gql`
  query GetPortalTextbook($teamId: String!, $slug: String!, $countView: Boolean) {
    portalTextbook(teamId: $teamId, slug: $slug, countView: $countView) {
      id
      teamId
      slug
      title
      excerpt
      content
      coverImageUrl
      authorName
      authorAvatarUrl
      category
      tags
      isBeta
      isPublished
      displayOrder
      publishedAt
      viewCount
      createdAt
      updatedAt
    }
  }
`;

/** Metadata-only companion to GET_PORTAL_TEXTBOOK_QUERY for related cards. */
export const GET_RELATED_PORTAL_TEXTBOOKS_QUERY = gql`
  query GetRelatedPortalTextbooks(
    $teamId: String!
    $excludeSlug: String!
    $limit: Int!
  ) {
    portalTextbooks(teamId: $teamId, excludeSlug: $excludeSlug, limit: $limit) {
      id
      slug
      title
      coverImageUrl
      category
      tags
    }
  }
`;

export interface PortalTextbook {
  id: string;
  teamId: string;
  slug: string;
  title: string;
  excerpt?: string | null;
  content?: string | null;
  coverImageUrl?: string | null;
  authorName?: string | null;
  authorAvatarUrl?: string | null;
  category?: string | null;
  tags?: string[] | null;
  isBeta: boolean;
  isPublished: boolean;
  displayOrder: number;
  publishedAt?: string | null;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
  /** 本文の字数 (コードの塊と画像は数えない)。一覧の取得 (portalTextbooks) だけが返す。サーバーが本文から数える */
  bodyChars?: number | null;
}

export type RelatedPortalTextbook = Pick<
  PortalTextbook,
  "id" | "slug" | "title" | "coverImageUrl" | "category" | "tags"
>;

/**
 * テキスト教材一覧を取得
 */
export async function getPortalTextbooks(
  teamId: string,
  category?: string,
  options?: GraphqlTeamOptions,
): Promise<PortalTextbook[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_PORTAL_TEXTBOOKS_QUERY, {
    teamId,
    category,
  });
  return (data as any).portalTextbooks ?? [];
}

/**
 * テキスト教材詳細を取得（閲覧数を増やす）
 */
export async function getPortalTextbook(
  teamId: string,
  slug: string,
  options?: GraphqlTeamOptions & {
    /** false なら閲覧数を足さない (教科書の章ごとの頁)。省けば api-go が足す */
    countView?: boolean;
  },
): Promise<PortalTextbook | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_PORTAL_TEXTBOOK_QUERY, {
    teamId,
    slug,
    ...(options?.countView === false ? { countView: false } : {}),
  });
  return (data as any).portalTextbook ?? null;
}

/** Fetch at most seven metadata candidates for a six-card related rail. */
export async function getRelatedPortalTextbooks(
  teamId: string,
  excludeSlug: string,
  options?: GraphqlTeamOptions,
): Promise<RelatedPortalTextbook[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_RELATED_PORTAL_TEXTBOOKS_QUERY, {
    teamId,
    excludeSlug,
    limit: RELATED_TEAM_CONTENT_LIMIT,
  });
  return (data as any).portalTextbooks ?? [];
}

const CREATE_PORTAL_TEXTBOOK_MUTATION = gql`
  mutation CreatePortalTextbook($input: CreatePortalTextbookInput!) {
    createPortalTextbook(input: $input) {
      id
      teamId
      slug
      title
      excerpt
      content
      coverImageUrl
      authorName
      authorAvatarUrl
      category
      tags
      isBeta
      isPublished
      displayOrder
      publishedAt
      viewCount
      createdAt
      updatedAt
    }
  }
`;

export interface CreatePortalTextbookInput {
  teamId: string;
  title: string;
  slug: string;
  content: string;
  excerpt?: string;
  coverImageUrl?: string;
  authorName?: string;
  authorAvatarUrl?: string;
  category?: string;
  tags?: string[];
  isBeta?: boolean;
  isPublished?: boolean;
  displayOrder?: number;
}

/**
 * テキスト教材を作成（管理者のみ）
 */
export async function createPortalTextbook(
  input: CreatePortalTextbookInput,
  options?: GraphqlTeamOptions,
): Promise<PortalTextbook> {
  const client = ensureClient(options);
  const data = await client.request(CREATE_PORTAL_TEXTBOOK_MUTATION, { input });
  return (data as any).createPortalTextbook;
}

const UPDATE_PORTAL_TEXTBOOK_MUTATION = gql`
  mutation UpdatePortalTextbook(
    $textbookId: String!
    $input: UpdatePortalTextbookInput!
  ) {
    updatePortalTextbook(textbookId: $textbookId, input: $input) {
      id
      teamId
      slug
      title
      excerpt
      content
      coverImageUrl
      authorName
      authorAvatarUrl
      category
      tags
      isBeta
      isPublished
      displayOrder
      publishedAt
      viewCount
      createdAt
      updatedAt
    }
  }
`;

export interface UpdatePortalTextbookInput {
  title?: string;
  slug?: string;
  excerpt?: string;
  content?: string;
  category?: string;
  tags?: string[];
  isBeta?: boolean;
  isPublished?: boolean;
  displayOrder?: number;
}

/**
 * テキスト教材を更新（管理者のみ）
 */
export async function updatePortalTextbook(
  textbookId: string,
  input: UpdatePortalTextbookInput,
  options?: GraphqlTeamOptions,
): Promise<PortalTextbook> {
  const client = ensureClient(options);
  const data = await client.request(UPDATE_PORTAL_TEXTBOOK_MUTATION, {
    textbookId,
    input,
  });
  return (data as any).updatePortalTextbook;
}

// ========== Update Team Assignment ==========

const UPDATE_TEAM_ASSIGNMENT_MUTATION = gql`
  mutation UpdateTeamAssignment(
    $assignmentId: String!
    $input: UpdateTeamAssignmentInput!
  ) {
    updateTeamAssignment(assignmentId: $assignmentId, input: $input) {
      id
      teamId
      title
      description
      instructions
      type
      isRequired
      displayOrder
      deadline
      maxPoints
      relativeMonth
      relativeDay
      figmaUrl
      loomUrl
      tag
      createdBy
      createdAt
      updatedAt
    }
  }
`;

export interface UpdateTeamAssignmentInput {
  title?: string;
  description?: string | null;
  instructions?: string | null;
  isRequired?: boolean;
  maxPoints?: number | null;
  relativeMonth?: number | null;
  relativeDay?: number | null;
  figmaUrl?: string | null;
  loomUrl?: string | null;
  tag?: string | null;
}

/**
 * チームの課題を更新（管理者のみ）
 */
export async function updateTeamAssignment(
  assignmentId: string,
  input: UpdateTeamAssignmentInput,
  options?: GraphqlTeamOptions,
): Promise<TeamAssignment> {
  const client = ensureClient(options);
  const data = await client.request(UPDATE_TEAM_ASSIGNMENT_MUTATION, {
    assignmentId,
    input,
  });
  return (data as any).updateTeamAssignment;
}

// ========== Luagate User Profile ==========

const GET_MY_LUAGATE_PROFILE_QUERY = gql`
  query GetMyLuagateProfile {
    myLuagateProfile {
      id
      userId
      enrollmentDate
      planType
      status
      targetGraduationDate
      adminNote
      createdAt
      updatedAt
    }
  }
`;

export interface LuagateUserProfile {
  id: string;
  userId: string;
  enrollmentDate: string;
  planType: string;
  status: string;
  targetGraduationDate?: string | null;
  adminNote?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * 自分のLuagateプロフィールを取得
 */
export async function getMyLuagateProfile(
  options?: GraphqlTeamOptions,
): Promise<LuagateUserProfile | null> {
  const client = ensureClient(options);
  try {
    const data = await client.request(GET_MY_LUAGATE_PROFILE_QUERY);
    return (data as any).myLuagateProfile ?? null;
  } catch {
    // プロフィールが存在しない場合はnullを返す
    return null;
  }
}

// ========== Team Announcements (お知らせ) ==========

const GET_TEAM_ANNOUNCEMENTS_QUERY = gql`
  query GetTeamAnnouncements($teamId: String!, $limit: Int) {
    teamAnnouncements(teamId: $teamId, limit: $limit) {
      id
      teamId
      type
      title
      message
      linkUrl
      isPublished
      publishedAt
      createdAt
      updatedAt
    }
  }
`;

export interface TeamAnnouncement {
  id: string;
  teamId: string;
  type: string;
  title: string;
  message: string;
  linkUrl?: string | null;
  isPublished: boolean;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * チームのお知らせ一覧を取得
 */
export async function getTeamAnnouncements(
  teamId: string,
  limit: number = 10,
  options?: GraphqlTeamOptions,
): Promise<TeamAnnouncement[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_ANNOUNCEMENTS_QUERY, {
    teamId,
    limit,
  });
  return (data as any).teamAnnouncements ?? [];
}

// ========== Team Curriculum Items (カリキュラム) ==========

const GET_TEAM_CURRICULUM_ITEMS_QUERY = gql`
  query GetTeamCurriculums($teamId: String!) {
    teamCurriculums(teamId: $teamId) {
      id
      teamId
      lpCourseId
      sectionId
      title
      description
      content
      imageUrl
      lessonCoverUrl
      itemType
      lectureId
      courseId
      assignmentId
      displayOrder
      isPublished
      difficulty
      isBeta
      playgroundLanguage
      pathMode
      createdAt
      updatedAt
      homeworkCount
    }
  }
`;

const GET_TEAM_CURRICULUM_BY_ID_QUERY = gql`
  query GetTeamCurriculumById($id: String!) {
    teamCurriculumById(id: $id) {
      id
      teamId
      lpCourseId
      sectionId
      title
      description
      content
      imageUrl
      itemType
      lectureId
      courseId
      assignmentId
      displayOrder
      isPublished
      difficulty
      isBeta
      playgroundLanguage
      pathMode
      createdAt
      updatedAt
      homeworkCount
    }
  }
`;

const GET_TEAM_CURRICULUM_BY_ORDER_QUERY = gql`
  query GetTeamCurriculumByOrder(
    $teamId: String!
    $displayOrder: Int!
    $lpCourseId: String
    $defaultCourseOnly: Boolean
  ) {
    teamCurriculumByOrder(
      teamId: $teamId
      displayOrder: $displayOrder
      lpCourseId: $lpCourseId
      defaultCourseOnly: $defaultCourseOnly
    ) {
      id
      teamId
      lpCourseId
      sectionId
      title
      description
      content
      imageUrl
      itemType
      lectureId
      courseId
      assignmentId
      displayOrder
      isPublished
      difficulty
      isBeta
      playgroundLanguage
      pathMode
      createdAt
      updatedAt
      homeworkCount
    }
  }
`;

export interface TeamCurriculum {
  id: string;
  teamId: string;
  lpCourseId?: string | null;
  sectionId?: string | null;
  title: string;
  description?: string | null;
  content?: string | null;
  imageUrl?: string | null;
  /**
   * このレッスン 1 本ぶんの絵。**imageUrl とは別物**。
   *
   * imageUrl には受講生に出さないと決めた使い回しの人物イラストが入って
   * いて、1 枚が 13 本で共有されている。出してよいのはこちらだけ。
   */
  lessonCoverUrl?: string | null;
  itemType: string;
  lectureId?: string | null;
  courseId?: string | null;
  assignmentId?: string | null;
  displayOrder: number;
  isPublished: boolean;
  difficulty?: string | null;
  isBeta: boolean;
  playgroundLanguage?: string | null;
  /**
   * 学び方。null は通常、"read" は読むだけ、"optional" は任意。
   * api-go が古くて来ないときも通常として扱う (lib/luagate/path-mode.ts)
   */
  pathMode?: string | null;
  createdAt: string;
  updatedAt: string;
  homeworkCount: number;
}

/**
 * チームのカリキュラム一覧を取得
 */
export async function getTeamCurriculums(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculum[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_CURRICULUM_ITEMS_QUERY, {
    teamId,
  });
  return (data as any).teamCurriculums ?? [];
}

// ========== Team LP Courses (学習パスを束ねる上位コース) ==========

export interface TeamLpCourse {
  id: string;
  teamId: string;
  title: string;
  slug: string;
  description?: string | null;
  coverImage?: string | null;
  difficulty?: string | null;
  isBeta: boolean;
  displayOrder: number;
  isPublished: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface TeamPathCourseCatalogItem {
  id: string;
  title: string;
  slug: string;
  description?: string | null;
  coverImage?: string | null;
  difficulty?: string | null;
  isBeta: boolean;
  displayOrder: number;
  isPublished: boolean;
  publishedSteps: number;
}

export interface TeamPathCourseCatalog {
  defaultPublishedSteps: number;
  courses: TeamPathCourseCatalogItem[];
}

export const GET_TEAM_PATH_COURSE_CATALOG_QUERY = gql`
  query GetTeamPathCourseCatalog($teamId: String!) {
    teamPathCourseCatalog(teamId: $teamId) {
      defaultPublishedSteps
      courses {
        id
        title
        slug
        description
        coverImage
        difficulty
        isBeta
        displayOrder
        isPublished
        publishedSteps
      }
    }
  }
`;

/**
 * Returns the default Path count and visible named Path cards in one request.
 * Curriculum bodies and per-Path curriculum lists are intentionally absent.
 */
export async function getTeamPathCourseCatalog(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamPathCourseCatalog> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_PATH_COURSE_CATALOG_QUERY, {
    teamId,
  });
  return (
    (data as any).teamPathCourseCatalog ?? {
      defaultPublishedSteps: 0,
      courses: [],
    }
  );
}

export interface TeamPathListItem {
  id: string;
  lpCourseId?: string | null;
  sectionId?: string | null;
  title: string;
  description?: string | null;
  itemType: string;
  displayOrder: number;
  isPublished: boolean;
  isBeta: boolean;
  updatedAt: string;
  homeworkCount: number;
  lessonCoverUrl?: string | null;
  /**
   * 学び方。null は通常、"read" は読むだけ、"optional" は任意。
   * api-go が古くて来ないときも通常として扱う (lib/luagate/path-mode.ts)
   */
  pathMode?: string | null;
}

export interface TeamPathListCourse {
  id: string;
  title: string;
  slug: string;
  displayOrder: number;
  isPublished: boolean;
}

export interface TeamPathListSection {
  id: string;
  lpCourseId?: string | null;
  title: string;
  displayOrder: number;
}

export interface TeamPathListProgress {
  curriculumId: string;
  isCompleted: boolean;
}

export interface TeamPathListLearningAsset {
  kind: "learn_course" | "learn_lesson";
  relationType:
    | "primary"
    | "prerequisite"
    | "supplement"
    | "demonstration"
    | "practice"
    | "assessment"
    | "review"
    | "reference";
  coverageRole:
    | "introduce"
    | "explain"
    | "demonstrate"
    | "practice"
    | "apply"
    | "assess"
    | "review"
    | "reference";
  required: boolean;
  displayOrder: number;
  course: {
    id: string;
    slug: string;
    title: string;
  };
  lesson?: {
    id: string;
    slug: string;
    title: string;
  } | null;
}

export interface TeamPathListLearningAssetIndexEntry {
  curriculumId: string;
  assets: TeamPathListLearningAsset[];
}

export interface TeamPathListBundle {
  canManage: boolean;
  items: TeamPathListItem[];
  courses: TeamPathListCourse[];
  sections: TeamPathListSection[];
  progress: TeamPathListProgress[];
  learningAssetIndex: TeamPathListLearningAssetIndexEntry[];
}

export const GET_TEAM_PATH_LIST_BUNDLE_QUERY = gql`
  query GetTeamPathListBundle($teamId: String!) {
    teamPathListBundle(teamId: $teamId) {
      canManage
      items {
        id
        lpCourseId
        sectionId
        title
        description
        itemType
        displayOrder
        isPublished
        isBeta
        updatedAt
        homeworkCount
        lessonCoverUrl
        pathMode
      }
      courses {
        id
        title
        slug
        displayOrder
        isPublished
      }
      sections {
        id
        lpCourseId
        title
        displayOrder
      }
      progress {
        curriculumId
        isCompleted
      }
      learningAssetIndex {
        curriculumId
        assets {
          kind
          relationType
          coverageRole
          required
          displayOrder
          course {
            id
            slug
            title
          }
          lesson {
            id
            slug
            title
          }
        }
      }
    }
  }
`;

/**
 * Loads /luagate/path/list in one GraphQL operation. Curriculum content and
 * the unused rich Course/Lesson asset fields are intentionally absent.
 */
export async function getTeamPathListBundle(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamPathListBundle> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_PATH_LIST_BUNDLE_QUERY, {
    teamId,
  });
  return (
    (data as any).teamPathListBundle ?? {
      canManage: false,
      items: [],
      courses: [],
      sections: [],
      progress: [],
      learningAssetIndex: [],
    }
  );
}

const TEAM_LP_COURSE_FIELDS = `
  id
  teamId
  title
  slug
  description
  coverImage
  difficulty
  isBeta
  displayOrder
  isPublished
  createdAt
  updatedAt
`;

const GET_TEAM_LP_COURSES_QUERY = gql`
  query GetTeamLpCourses($teamId: String!, $includeUnpublished: Boolean) {
    teamLpCourses(teamId: $teamId, includeUnpublished: $includeUnpublished) {
      ${TEAM_LP_COURSE_FIELDS}
    }
  }
`;

export async function getTeamLpCourses(
  teamId: string,
  includeUnpublished?: boolean,
  options?: GraphqlTeamOptions,
): Promise<TeamLpCourse[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_LP_COURSES_QUERY, {
    teamId,
    includeUnpublished: includeUnpublished ?? false,
  });
  return (data as any).teamLpCourses ?? [];
}

export type PublicPathChapter = {
  chapterNumber: number;
  title: string;
  startDisplayOrder: number;
  endDisplayOrder: number;
};

const GET_PUBLIC_PATH_CHAPTERS_QUERY = gql`
  query GetPublicPathChapters($teamId: String!) {
    publicPathChapters(teamId: $teamId) {
      chapterNumber
      title
      startDisplayOrder
      endDisplayOrder
    }
  }
`;

/**
 * 学習パスの章一覧。**ログイン不要**。
 *
 * 共有 URL の OGP カードを出すためだけに使う。レッスンの一覧や本文は返らない。
 * 認証付きの画面では teamCurriculumSections 側を使うこと。
 */
export async function getPublicPathChapters(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<PublicPathChapter[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_PUBLIC_PATH_CHAPTERS_QUERY, { teamId });
  return (data as any).publicPathChapters ?? [];
}

const GET_PUBLIC_PATH_LESSON_COVER_QUERY = gql`
  query GetPublicPathLessonCover($teamId: String!, $displayOrder: Int!) {
    publicPathLessonCover(teamId: $teamId, displayOrder: $displayOrder)
  }
`;

/**
 * レッスン 1 本ぶんの絵。**ログイン不要**。
 *
 * 共有 URL の OGP カードに出すためだけに使う。絵が無ければ null で、
 * 呼び出し側が章の絵へ落とす。題名も本文も返らない。
 */
export async function getPublicPathLessonCover(
  teamId: string,
  displayOrder: number,
  options?: GraphqlTeamOptions,
): Promise<string | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_PUBLIC_PATH_LESSON_COVER_QUERY, {
    teamId,
    displayOrder,
  });
  return (data as any).publicPathLessonCover ?? null;
}

/**
 * Learner-facing curriculum projection shared by the Path catalog and detail
 * navigation. Published quiz rows are included for checkpoint cards; detail
 * navigation filters them. `content` is intentionally not part of this type or
 * query.
 */
export interface TeamCurriculumSummary {
  id: string;
  lpCourseId?: string | null;
  sectionId?: string | null;
  title: string;
  description?: string | null;
  imageUrl?: string | null;
  itemType: string;
  lectureId?: string | null;
  courseId?: string | null;
  assignmentId?: string | null;
  displayOrder: number;
  isPublished: boolean;
  difficulty?: string | null;
  isBeta: boolean;
  playgroundLanguage?: string | null;
  homeworkCount: number;
  /** 学び方。null は通常、"read" は読むだけ、"optional" は任意 */
  pathMode?: string | null;
}

export interface TeamCurriculumSummaryResult {
  items: TeamCurriculumSummary[];
  totalCount: number;
}

export interface TeamCurriculumDashboardItem {
  id: string;
  assignmentId?: string | null;
  isPublished: boolean;
  /** 区切り (milestone) は完了数の分母から外す。api-go が古いと来ない */
  itemType?: string | null;
  /** 学び方。"optional" (任意) は完了数の分母から外す */
  pathMode?: string | null;
}

export const GET_TEAM_CURRICULUM_DASHBOARD_CATALOG_QUERY = gql`
  query GetTeamCurriculumDashboardCatalog($teamId: String!) {
    teamCurriculumDashboardCatalog(teamId: $teamId) {
      id
      assignmentId
      isPublished
      itemType
      pathMode
    }
  }
`;

/**
 * Returns the content-free, all-Path projection needed by dashboard counters
 * in one GraphQL request. The API resolves it with one catalog SQL after the
 * membership check.
 */
export async function getTeamCurriculumDashboardCatalog(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumDashboardItem[]> {
  const client = ensureClient(options);
  const data = await client.request(
    GET_TEAM_CURRICULUM_DASHBOARD_CATALOG_QUERY,
    { teamId },
  );
  return (data as any).teamCurriculumDashboardCatalog ?? [];
}

export const GET_TEAM_CURRICULUM_SUMMARIES_QUERY = gql`
  query GetTeamCurriculumSummaries(
    $teamId: String!
    $lpCourseId: String
    $limit: Int
    $offset: Int
  ) {
    teamCurriculumSummaries(
      teamId: $teamId
      lpCourseId: $lpCourseId
      limit: $limit
      offset: $offset
    ) {
      items {
        id
        lpCourseId
        sectionId
        title
        description
        imageUrl
        itemType
        lectureId
        courseId
        assignmentId
        displayOrder
        isPublished
        difficulty
        isBeta
        playgroundLanguage
        pathMode
        homeworkCount
      }
      totalCount
    }
  }
`;

/**
 * Published summaries, including quiz rows, for a single Path course. Pass
 * `null` for the default Path. A bare page is limited to 50 rows by the API;
 * explicit callers may request up to 5,000 rows without transferring lesson
 * bodies.
 */
export async function getTeamCurriculumSummaries(
  teamId: string,
  lpCourseId: string | null,
  limit = 50,
  offset = 0,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumSummaryResult> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_CURRICULUM_SUMMARIES_QUERY, {
    teamId,
    lpCourseId,
    limit,
    offset,
  });
  return (data as any).teamCurriculumSummaries ?? { items: [], totalCount: 0 };
}

/**
 * Reads a complete lightweight Path, including published quiz rows, in one
 * bounded request. A Path larger than the explicit 5,000-row transport limit
 * fails instead of returning a partial list or reintroducing request/SQL
 * fan-out.
 */
export const TEAM_CURRICULUM_SUMMARY_MAX_ITEMS = 5000;

export async function getAllTeamCurriculumSummaries(
  teamId: string,
  lpCourseId: string | null,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumSummaryResult> {
  const result = await getTeamCurriculumSummaries(
    teamId,
    lpCourseId,
    TEAM_CURRICULUM_SUMMARY_MAX_ITEMS,
    0,
    options,
  );
  // Keep the legacy empty-page behavior for a temporarily inconsistent
  // backend response. A non-empty truncated response, however, must fail
  // closed: callers use this result for ordering, adjacent navigation, and
  // publication checks and cannot safely render only the first segment.
  if (result.items.length > 0 && result.items.length < result.totalCount) {
    throw new Error(
      `Path exceeds the ${TEAM_CURRICULUM_SUMMARY_MAX_ITEMS}-item summary limit`,
    );
  }
  return result;
}

const GET_TEAM_CURRICULUMS_BY_LP_COURSE_QUERY = gql`
  query GetTeamCurriculumsByLpCourse($teamId: String!, $lpCourseId: String) {
    teamCurriculumsByLpCourse(teamId: $teamId, lpCourseId: $lpCourseId) {
      id
      teamId
      lpCourseId
      sectionId
      title
      description
      content
      imageUrl
      itemType
      lectureId
      courseId
      assignmentId
      displayOrder
      isPublished
      difficulty
      isBeta
      playgroundLanguage
      pathMode
      createdAt
      updatedAt
      homeworkCount
    }
  }
`;

export async function getTeamCurriculumsByLpCourse(
  teamId: string,
  lpCourseId: string | null,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculum[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_CURRICULUMS_BY_LP_COURSE_QUERY, {
    teamId,
    lpCourseId,
  });
  return (data as any).teamCurriculumsByLpCourse ?? [];
}

const CREATE_TEAM_LP_COURSE_MUTATION = gql`
  mutation CreateTeamLpCourse(
    $teamId: String!
    $title: String!
    $slug: String!
    $description: String
    $coverImage: String
    $difficulty: String
    $isBeta: Boolean
    $displayOrder: Int
    $isPublished: Boolean
  ) {
    createTeamLpCourse(
      teamId: $teamId
      title: $title
      slug: $slug
      description: $description
      coverImage: $coverImage
      difficulty: $difficulty
      isBeta: $isBeta
      displayOrder: $displayOrder
      isPublished: $isPublished
    ) {
      ${TEAM_LP_COURSE_FIELDS}
    }
  }
`;

export async function createTeamLpCourse(
  input: {
    teamId: string;
    title: string;
    slug: string;
    description?: string | null;
    coverImage?: string | null;
    difficulty?: string | null;
    isBeta?: boolean;
    displayOrder?: number;
    isPublished?: boolean;
  },
  options?: GraphqlTeamOptions,
): Promise<TeamLpCourse> {
  const client = ensureClient(options);
  const data = await client.request(CREATE_TEAM_LP_COURSE_MUTATION, input);
  return (data as any).createTeamLpCourse;
}

const UPDATE_TEAM_LP_COURSE_MUTATION = gql`
  mutation UpdateTeamLpCourse(
    $id: String!
    $teamId: String!
    $title: String
    $slug: String
    $description: String
    $coverImage: String
    $difficulty: String
    $isBeta: Boolean
    $displayOrder: Int
    $isPublished: Boolean
  ) {
    updateTeamLpCourse(
      id: $id
      teamId: $teamId
      title: $title
      slug: $slug
      description: $description
      coverImage: $coverImage
      difficulty: $difficulty
      isBeta: $isBeta
      displayOrder: $displayOrder
      isPublished: $isPublished
    ) {
      ${TEAM_LP_COURSE_FIELDS}
    }
  }
`;

export async function updateTeamLpCourse(
  id: string,
  input: {
    teamId: string;
    title?: string;
    slug?: string;
    description?: string | null;
    coverImage?: string | null;
    difficulty?: string | null;
    isBeta?: boolean;
    displayOrder?: number;
    isPublished?: boolean;
  },
  options?: GraphqlTeamOptions,
): Promise<TeamLpCourse> {
  const client = ensureClient(options);
  const data = await client.request(UPDATE_TEAM_LP_COURSE_MUTATION, {
    id,
    ...input,
  });
  return (data as any).updateTeamLpCourse;
}

const DELETE_TEAM_LP_COURSE_MUTATION = gql`
  mutation DeleteTeamLpCourse($id: String!, $teamId: String!) {
    deleteTeamLpCourse(id: $id, teamId: $teamId)
  }
`;

export async function deleteTeamLpCourse(
  id: string,
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_TEAM_LP_COURSE_MUTATION, {
    id,
    teamId,
  });
  return (data as any).deleteTeamLpCourse ?? false;
}

export interface TeamCurriculumPaginatedResult {
  items: TeamCurriculum[];
  totalCount: number;
}

const GET_TEAM_CURRICULUM_ITEMS_PAGINATED_QUERY = gql`
  query GetTeamCurriculumsPaginated(
    $teamId: String!
    $limit: Int
    $offset: Int
  ) {
    teamCurriculumsPaginated(teamId: $teamId, limit: $limit, offset: $offset) {
      items {
        id
        teamId
        sectionId
        title
        description
        content
        imageUrl
        itemType
        lectureId
        courseId
        assignmentId
        displayOrder
        isPublished
        difficulty
        isBeta
        createdAt
        updatedAt
        homeworkCount
      }
      totalCount
    }
  }
`;

export async function getTeamCurriculumsPaginated(
  teamId: string,
  limit: number,
  offset: number,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumPaginatedResult> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_CURRICULUM_ITEMS_PAGINATED_QUERY, {
    teamId,
    limit,
    offset,
  });
  return (data as any).teamCurriculumsPaginated ?? { items: [], totalCount: 0 };
}

/**
 * IDでカリキュラムを取得
 */
export async function getTeamCurriculumById(
  id: string,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculum | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_CURRICULUM_BY_ID_QUERY, { id });
  return (data as any).teamCurriculumById ?? null;
}

/**
 * displayOrderでカリキュラムを取得
 */
export async function getTeamCurriculumByOrder(
  teamId: string,
  displayOrder: number,
  options?: GraphqlTeamOptions & {
    // コース絞り込み。
    //   lpCourseId に文字列      → 該当コースに限定
    //   defaultCourseOnly: true → 既定コース (lp_course_id IS NULL) に限定
    //   どちらも未指定          → コース無視で displayOrder のみ (従来挙動)
    // 注意: 既定コースに絞るには defaultCourseOnly を使う。lpCourseId: null だけでは絞られない。
    lpCourseId?: string | null;
    defaultCourseOnly?: boolean;
  },
): Promise<TeamCurriculum | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_CURRICULUM_BY_ORDER_QUERY, {
    teamId,
    displayOrder,
    lpCourseId: options?.lpCourseId ?? null,
    defaultCourseOnly: options?.defaultCourseOnly ?? false,
  });
  return (data as any).teamCurriculumByOrder ?? null;
}

// ========== Team Curriculum Item Mutations ==========

const CREATE_TEAM_CURRICULUM_ITEM_MUTATION = gql`
  mutation CreateTeamCurriculum(
    $teamId: String!
    $title: String!
    $description: String
    $content: String
    $imageUrl: String
    $itemType: String
    $lectureId: String
    $courseId: String
    $assignmentId: String
    $displayOrder: Int
    $isPublished: Boolean
    $difficulty: String
    $isBeta: Boolean
    $sectionId: String
  ) {
    createTeamCurriculum(
      teamId: $teamId
      title: $title
      description: $description
      content: $content
      imageUrl: $imageUrl
      itemType: $itemType
      lectureId: $lectureId
      courseId: $courseId
      assignmentId: $assignmentId
      displayOrder: $displayOrder
      isPublished: $isPublished
      difficulty: $difficulty
      isBeta: $isBeta
      sectionId: $sectionId
    ) {
      id
      teamId
      sectionId
      title
      description
      content
      imageUrl
      itemType
      lectureId
      courseId
      assignmentId
      displayOrder
      isPublished
      difficulty
      isBeta
      createdAt
      updatedAt
    }
  }
`;

const UPDATE_TEAM_CURRICULUM_ITEM_MUTATION = gql`
  mutation UpdateTeamCurriculum(
    $id: String!
    $title: String
    $description: String
    $content: String
    $imageUrl: String
    $lessonCoverUrl: String
    $itemType: String
    $lectureId: String
    $courseId: String
    $assignmentId: String
    $displayOrder: Int
    $isPublished: Boolean
    $difficulty: String
    $isBeta: Boolean
    $sectionId: String
    $pathMode: String
  ) {
    updateTeamCurriculum(
      id: $id
      title: $title
      description: $description
      content: $content
      imageUrl: $imageUrl
      lessonCoverUrl: $lessonCoverUrl
      itemType: $itemType
      lectureId: $lectureId
      courseId: $courseId
      assignmentId: $assignmentId
      displayOrder: $displayOrder
      isPublished: $isPublished
      difficulty: $difficulty
      isBeta: $isBeta
      sectionId: $sectionId
      pathMode: $pathMode
    ) {
      id
      teamId
      sectionId
      title
      description
      content
      imageUrl
      lessonCoverUrl
      itemType
      lectureId
      courseId
      assignmentId
      displayOrder
      isPublished
      difficulty
      isBeta
      pathMode
      createdAt
      updatedAt
    }
  }
`;

const DELETE_TEAM_CURRICULUM_ITEM_MUTATION = gql`
  mutation DeleteTeamCurriculum($id: String!) {
    deleteTeamCurriculum(id: $id)
  }
`;

export interface CreateTeamCurriculumInput {
  teamId: string;
  title: string;
  description?: string | null;
  content?: string | null;
  imageUrl?: string | null;
  itemType?: string;
  lectureId?: string | null;
  courseId?: string | null;
  assignmentId?: string | null;
  displayOrder?: number;
  isPublished?: boolean;
  difficulty?: string | null;
  isBeta?: boolean;
  sectionId?: string | null;
}

export interface UpdateTeamCurriculumInput {
  id: string;
  title?: string;
  description?: string | null;
  content?: string | null;
  imageUrl?: string | null;
  /**
   * レッスン 1 本ぶんの絵。**画面に出るのはこちら。**
   * imageUrl は使い回しの人物イラストが入っていて描画を止めてある。
   * 空文字を渡すと絵を外す。
   */
  lessonCoverUrl?: string | null;
  itemType?: string;
  lectureId?: string | null;
  courseId?: string | null;
  assignmentId?: string | null;
  displayOrder?: number;
  isPublished?: boolean;
  difficulty?: string | null;
  isBeta?: boolean;
  sectionId?: string | null;
  /**
   * 学び方。"read" は読むだけ、"optional" は任意、空文字は通常に戻す。
   * 省くと今の値のまま。
   */
  pathMode?: string;
}

/**
 * カリキュラムアイテムを作成
 */
export async function createTeamCurriculum(
  input: CreateTeamCurriculumInput,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculum> {
  const client = ensureClient(options);
  const data = await client.request(
    CREATE_TEAM_CURRICULUM_ITEM_MUTATION,
    input,
  );
  return (data as any).createTeamCurriculum;
}

/**
 * カリキュラムアイテムを更新
 */
export async function updateTeamCurriculum(
  input: UpdateTeamCurriculumInput,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculum> {
  const client = ensureClient(options);
  const data = await client.request(
    UPDATE_TEAM_CURRICULUM_ITEM_MUTATION,
    input,
  );
  return (data as any).updateTeamCurriculum;
}

/**
 * カリキュラムアイテムを削除
 */
export async function deleteTeamCurriculum(
  id: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_TEAM_CURRICULUM_ITEM_MUTATION, {
    id,
  });
  return (data as any).deleteTeamCurriculum;
}

// ========== Team Curriculum Sections (学習パスの順番グループ見出し) ==========

export interface TeamCurriculumSection {
  id: string;
  teamId: string;
  lpCourseId?: string | null;
  title: string;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

const TEAM_CURRICULUM_SECTION_FIELDS = `
  id
  teamId
  lpCourseId
  title
  displayOrder
  createdAt
  updatedAt
`;

const GET_TEAM_CURRICULUM_SECTIONS_QUERY = gql`
  query GetTeamCurriculumSections($teamId: String!, $lpCourseId: String) {
    teamCurriculumSections(teamId: $teamId, lpCourseId: $lpCourseId) {
      ${TEAM_CURRICULUM_SECTION_FIELDS}
    }
  }
`;

export async function getTeamCurriculumSections(
  teamId: string,
  lpCourseId: string | null,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumSection[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_CURRICULUM_SECTIONS_QUERY, {
    teamId,
    lpCourseId,
  });
  return (data as any).teamCurriculumSections ?? [];
}

const CREATE_TEAM_CURRICULUM_SECTION_MUTATION = gql`
  mutation CreateTeamCurriculumSection(
    $teamId: String!
    $lpCourseId: String
    $title: String!
    $displayOrder: Int
  ) {
    createTeamCurriculumSection(
      teamId: $teamId
      lpCourseId: $lpCourseId
      title: $title
      displayOrder: $displayOrder
    ) {
      ${TEAM_CURRICULUM_SECTION_FIELDS}
    }
  }
`;

export async function createTeamCurriculumSection(
  input: {
    teamId: string;
    lpCourseId?: string | null;
    title: string;
    displayOrder?: number;
  },
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumSection> {
  const client = ensureClient(options);
  const data = await client.request(
    CREATE_TEAM_CURRICULUM_SECTION_MUTATION,
    input,
  );
  return (data as any).createTeamCurriculumSection;
}

const UPDATE_TEAM_CURRICULUM_SECTION_MUTATION = gql`
  mutation UpdateTeamCurriculumSection($id: ID!, $title: String, $displayOrder: Int) {
    updateTeamCurriculumSection(id: $id, title: $title, displayOrder: $displayOrder) {
      ${TEAM_CURRICULUM_SECTION_FIELDS}
    }
  }
`;

export async function updateTeamCurriculumSection(
  id: string,
  input: { title?: string; displayOrder?: number },
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumSection> {
  const client = ensureClient(options);
  const data = await client.request(UPDATE_TEAM_CURRICULUM_SECTION_MUTATION, {
    id,
    ...input,
  });
  return (data as any).updateTeamCurriculumSection;
}

const DELETE_TEAM_CURRICULUM_SECTION_MUTATION = gql`
  mutation DeleteTeamCurriculumSection($id: ID!) {
    deleteTeamCurriculumSection(id: $id)
  }
`;

export async function deleteTeamCurriculumSection(
  id: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_TEAM_CURRICULUM_SECTION_MUTATION, {
    id,
  });
  return (data as any).deleteTeamCurriculumSection ?? false;
}

// ========== Team Curriculum Revisions (本文の編集履歴) ==========

export interface TeamCurriculumRevision {
  id: string;
  curriculumId: string;
  teamId: string;
  displayOrder: number;
  title: string;
  content: string;
  updatedBy: string | null;
  createdAt: string;
}

const LIST_TEAM_CURRICULUM_REVISIONS_QUERY = gql`
  query TeamCurriculumRevisions($curriculumId: String!, $limit: Int) {
    teamCurriculumRevisions(curriculumId: $curriculumId, limit: $limit) {
      id
      curriculumId
      teamId
      displayOrder
      title
      content
      updatedBy
      createdAt
    }
  }
`;

const GET_TEAM_CURRICULUM_REVISION_QUERY = gql`
  query TeamCurriculumRevision($id: String!) {
    teamCurriculumRevision(id: $id) {
      id
      curriculumId
      teamId
      displayOrder
      title
      content
      updatedBy
      createdAt
    }
  }
`;

const RESTORE_TEAM_CURRICULUM_REVISION_MUTATION = gql`
  mutation RestoreTeamCurriculumRevision($revisionId: String!) {
    restoreTeamCurriculumRevision(revisionId: $revisionId) {
      id
      teamId
      title
      description
      content
      imageUrl
      itemType
      lectureId
      courseId
      assignmentId
      displayOrder
      isPublished
      createdAt
      updatedAt
    }
  }
`;

/**
 * カリキュラム本文の履歴一覧を取得
 */
export async function listTeamCurriculumRevisions(
  curriculumId: string,
  limit?: number,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumRevision[]> {
  const client = ensureClient(options);
  const data = await client.request(LIST_TEAM_CURRICULUM_REVISIONS_QUERY, {
    curriculumId,
    limit: limit ?? null,
  });
  return (data as any).teamCurriculumRevisions ?? [];
}

/**
 * カリキュラム本文の履歴 1 件を取得
 */
export async function getTeamCurriculumRevision(
  id: string,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumRevision | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_CURRICULUM_REVISION_QUERY, { id });
  return (data as any).teamCurriculumRevision ?? null;
}

/**
 * 履歴を本体に書き戻す（戻す前の状態は新しい revision として積まれる）
 */
export async function restoreTeamCurriculumRevision(
  revisionId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculum> {
  const client = ensureClient(options);
  const data = await client.request(RESTORE_TEAM_CURRICULUM_REVISION_MUTATION, {
    revisionId,
  });
  return (data as any).restoreTeamCurriculumRevision;
}

// ========== Team Curriculum Progress (カリキュラム進捗) ==========

const GET_MY_CURRICULUM_PROGRESS_QUERY = gql`
  query GetMyCurriculumProgress($teamId: String!) {
    myCurriculumProgress(teamId: $teamId) {
      id
      curriculumId
      userId
      isCompleted
      completedAt
      createdAt
      updatedAt
    }
  }
`;

const COMPLETE_CURRICULUM_MUTATION = gql`
  mutation CompleteCurriculum($curriculumId: String!) {
    completeCurriculum(curriculumId: $curriculumId) {
      id
      curriculumId
      userId
      isCompleted
      completedAt
      createdAt
      updatedAt
    }
  }
`;

// 学習パス v2 の項目の頁が済みにする口。読む項目のミニテストで選んだ番号を一緒に送り、サーバが済みの条件を見る
const COMPLETE_PATH_V2_CURRICULUM_MUTATION = gql`
  mutation CompletePathV2Curriculum($curriculumId: String!, $miniQuizAnswers: [Int!]) {
    completeCurriculum(curriculumId: $curriculumId, miniQuizAnswers: $miniQuizAnswers) {
      id
      curriculumId
      userId
      isCompleted
      completedAt
      createdAt
      updatedAt
    }
  }
`;

const UNCOMPLETE_CURRICULUM_MUTATION = gql`
  mutation UncompleteCurriculum($curriculumId: String!) {
    uncompleteCurriculum(curriculumId: $curriculumId)
  }
`;

export interface TeamCurriculumProgress {
  id: string;
  curriculumId: string;
  userId: string;
  isCompleted: boolean;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * 自分のカリキュラム進捗を取得
 */
export async function getMyCurriculumProgress(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumProgress[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_MY_CURRICULUM_PROGRESS_QUERY, {
    teamId,
  });
  return (data as any).myCurriculumProgress ?? [];
}

const GET_CURRICULUM_COMPLETION_STATS_QUERY = gql`
  query GetCurriculumCompletionStats($teamId: String!) {
    curriculumCompletionStats(teamId: $teamId) {
      totalLearners
      lessons {
        curriculumId
        title
        displayOrder
        itemType
        lpCourseId
        completedCount
      }
    }
  }
`;

export interface CurriculumLessonStat {
  curriculumId: string;
  title: string;
  displayOrder: number;
  itemType: string;
  lpCourseId: string | null;
  completedCount: number;
}

export interface CurriculumCompletionStats {
  totalLearners: number;
  lessons: CurriculumLessonStat[];
}

/**
 * 章別完了率の集計を取得 (admin のみ)。
 * 各レッスンの完了人数 + 在籍受講生母数を返す。
 */
export async function getCurriculumCompletionStats(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<CurriculumCompletionStats> {
  const client = ensureClient(options);
  const data = await client.request(GET_CURRICULUM_COMPLETION_STATS_QUERY, {
    teamId,
  });
  return (
    (data as any).curriculumCompletionStats ?? { totalLearners: 0, lessons: [] }
  );
}

// ========== BI: 日別実施件数 / チャレンジ別クリア状況 (admin) ==========

const GET_TEAM_DAILY_ACTIVITY_STATS_QUERY = gql`
  query GetTeamDailyActivityStats($teamId: String!, $days: Int) {
    teamDailyActivityStats(teamId: $teamId, days: $days) {
      date
      lessonCompletions
      challengeClears
    }
  }
`;

export interface DailyActivityStat {
  date: string; // YYYY-MM-DD (JST)
  lessonCompletions: number;
  challengeClears: number;
}

/**
 * 日別実施件数 (直近 days 日、レッスン完了 + チャレンジクリア) を取得 (admin のみ)。
 */
export async function getTeamDailyActivityStats(
  teamId: string,
  days?: number,
  options?: GraphqlTeamOptions,
): Promise<DailyActivityStat[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_TEAM_DAILY_ACTIVITY_STATS_QUERY, {
    teamId,
    days: days ?? 30,
  });
  return (data as any).teamDailyActivityStats ?? [];
}

// ========== BI: プラン別 (基本/生涯) 入会・売上 (admin) ==========

const GET_PLAN_ENROLLMENT_STATS_QUERY = gql`
  query GetPlanEnrollmentStats($teamId: String!, $months: Int) {
    planEnrollmentStats(teamId: $teamId, months: $months) {
      month
      planKey
      count
      revenue
    }
  }
`;

export interface PlanEnrollmentStat {
  month: string; // YYYY-MM (JST)
  planKey: "basic" | "lifetime";
  count: number;
  revenue: number; // 実支払額 amount の合計 (円)
}

/**
 * プラン別 (基本/生涯) の月次入会・売上 (直近 months ヶ月) を取得 (admin のみ)。
 */
export async function getPlanEnrollmentStats(
  teamId: string,
  months?: number,
  options?: GraphqlTeamOptions,
): Promise<PlanEnrollmentStat[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_PLAN_ENROLLMENT_STATS_QUERY, {
    teamId,
    months: months ?? 12,
  });
  return (data as any).planEnrollmentStats ?? [];
}

// ===== プラン管理 (メンバー個別 + 料金マスタ編集) =====

export interface MemberPlan {
  userId: string;
  /** prod_dev (実プロダクト開発演習) は主プランと並ぶ別の行で返る */
  planKey: "basic" | "lifetime" | "prod_dev" | null;
  amount: number | null;
  /** 演習 (prod_dev) を付けた時刻 (RFC3339)。主プランの行と記録が無い行は null */
  grantedAt?: string | null;
  /** 演習を付けた管理者の名前。主プランの行と記録が無い行は null */
  grantedByName?: string | null;
  /** 契約日 (YYYY-MM-DD)。myMemberPlans だけが読む。記録が無い行は null */
  contractedAt?: string | null;
}

export interface PlanMaster {
  id: string;
  planKey: "basic" | "lifetime" | "prod_dev";
  name: string;
  price: number;
  validFrom: string; // YYYY-MM-DD
  validTo: string | null;
}

const GET_MEMBER_PLANS_QUERY = gql`
  query MemberPlans($teamId: String!) {
    memberPlans(teamId: $teamId) {
      userId
      planKey
      amount
      grantedAt
      grantedByName
    }
  }
`;

/** チーム内メンバーの現在の有効プラン一覧を取得 (admin のみ)。 */
export async function getMemberPlans(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<MemberPlan[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_MEMBER_PLANS_QUERY, { teamId });
  return (data as any).memberPlans ?? [];
}

const GET_MY_MEMBER_PLANS_QUERY = gql`
  query MyMemberPlans($teamId: String!) {
    myMemberPlans(teamId: $teamId) {
      userId
      planKey
      contractedAt
    }
  }
`;

/**
 * ログインしている本人の、そのチームの active の契約の行 (主プランと演習)。
 * 支払い管理が、Stripe に毎月の契約が無い人の「ご契約中」を出すのに読む。体験の人・退会した人・メンバーでない人は空
 */
export async function getMyMemberPlans(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<MemberPlan[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_MY_MEMBER_PLANS_QUERY, { teamId });
  return (data as { myMemberPlans?: MemberPlan[] | null }).myMemberPlans ?? [];
}

const GET_PLAN_MASTERS_QUERY = gql`
  query PlanMasters($teamId: String!) {
    planMasters(teamId: $teamId) {
      id
      planKey
      name
      price
      validFrom
      validTo
    }
  }
`;

/** 料金マスタ (team_plans) 全行を取得 (admin のみ)。 */
export async function getPlanMasters(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<PlanMaster[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_PLAN_MASTERS_QUERY, { teamId });
  return (data as any).planMasters ?? [];
}

const SET_TEAM_MEMBER_PLAN_MUTATION = gql`
  mutation SetTeamMemberPlan(
    $teamId: String!
    $userId: String!
    $planKey: String
  ) {
    setTeamMemberPlan(teamId: $teamId, userId: $userId, planKey: $planKey) {
      userId
      planKey
      amount
    }
  }
`;

/** メンバーのプランを設定/変更/解除 (planKey=null で解約)。amount は自動算出。 */
export async function setTeamMemberPlan(
  teamId: string,
  userId: string,
  planKey: "basic" | "lifetime" | null,
  options?: GraphqlTeamOptions,
): Promise<MemberPlan> {
  const client = ensureClient(options);
  const data = await client.request(SET_TEAM_MEMBER_PLAN_MUTATION, {
    teamId,
    userId,
    planKey,
  });
  return (data as any).setTeamMemberPlan;
}

const SET_TEAM_MEMBER_PROD_DEV_MUTATION = gql`
  mutation SetTeamMemberProdDev($teamId: String!, $userId: String!, $granted: Boolean!) {
    setTeamMemberProdDev(teamId: $teamId, userId: $userId, granted: $granted)
  }
`;

/** 実プロダクト開発演習を付ける (granted=true) か外す。主プランは変えない。入金を確かめた管理者が使う */
export async function setTeamMemberProdDev(
  teamId: string,
  userId: string,
  granted: boolean,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request<{ setTeamMemberProdDev: boolean }>(
    SET_TEAM_MEMBER_PROD_DEV_MUTATION,
    { teamId, userId, granted },
  );
  return data.setTeamMemberProdDev;
}

const UPDATE_PLAN_MASTER_MUTATION = gql`
  mutation UpdatePlanMaster(
    $teamId: String!
    $id: String!
    $price: Int
    $validFrom: String
    $validTo: String
  ) {
    updatePlanMaster(
      teamId: $teamId
      id: $id
      price: $price
      validFrom: $validFrom
      validTo: $validTo
    ) {
      id
      planKey
      name
      price
      validFrom
      validTo
    }
  }
`;

/** 料金マスタ1行を更新 (price / validFrom / validTo)。validTo に空文字を渡すと null 化。 */
export async function updatePlanMaster(
  teamId: string,
  id: string,
  data: { price?: number; validFrom?: string; validTo?: string | null },
  options?: GraphqlTeamOptions,
): Promise<PlanMaster> {
  const client = ensureClient(options);
  const result = await client.request(UPDATE_PLAN_MASTER_MUTATION, {
    teamId,
    id,
    ...data,
  });
  return (result as any).updatePlanMaster;
}

const GET_CHALLENGE_COMPLETION_STATS_QUERY = gql`
  query GetChallengeCompletionStats($teamId: String!) {
    challengeCompletionStats(teamId: $teamId) {
      totalLearners
      challenges {
        challengeId
        title
        totalDays
        clearedCount
        startedCount
        dayClearCounts {
          day
          clearedCount
        }
      }
    }
  }
`;

export interface ChallengeDayClearStat {
  day: number;
  clearedCount: number;
}

export interface ChallengeCompletionStat {
  challengeId: string;
  title: string;
  totalDays: number;
  clearedCount: number;
  startedCount: number;
  dayClearCounts: ChallengeDayClearStat[];
}

export interface ChallengeCompletionStats {
  totalLearners: number;
  challenges: ChallengeCompletionStat[];
}

/**
 * チャレンジ別クリア状況の集計を取得 (admin のみ)。
 */
export async function getChallengeCompletionStats(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<ChallengeCompletionStats> {
  const client = ensureClient(options);
  const data = await client.request(GET_CHALLENGE_COMPLETION_STATS_QUERY, {
    teamId,
  });
  return (
    (data as any).challengeCompletionStats ?? {
      totalLearners: 0,
      challenges: [],
    }
  );
}

const GET_CONTENT_CLEAR_RANKING_QUERY = gql`
  query GetContentClearRanking($teamId: String!, $limit: Int) {
    contentClearRanking(teamId: $teamId, limit: $limit) {
      courses {
        courseId
        title
        slug
        publishStatus
        clearedCount
      }
      lessons {
        lessonId
        title
        courseId
        courseTitle
        clearedCount
      }
    }
  }
`;

export interface CourseClearStat {
  courseId: string;
  title: string;
  slug: string;
  publishStatus: string;
  clearedCount: number;
}

export interface LessonClearStat {
  lessonId: string;
  title: string;
  courseId: string;
  courseTitle: string;
  clearedCount: number;
}

export interface ContentClearRanking {
  courses: CourseClearStat[];
  lessons: LessonClearStat[];
}

/**
 * chotdekiru コース/レッスンのクリアランキングを取得 (admin のみ)。
 * team 非スコープ・全 chotdekiru ユーザー横断の集計。
 */
export async function getContentClearRanking(
  teamId: string,
  limit?: number,
  options?: GraphqlTeamOptions,
): Promise<ContentClearRanking> {
  const client = ensureClient(options);
  const data = await client.request(GET_CONTENT_CLEAR_RANKING_QUERY, {
    teamId,
    limit: limit ?? 10,
  });
  return (data as any).contentClearRanking ?? { courses: [], lessons: [] };
}

/**
 * カリキュラムをクリアする
 */
export async function completeCurriculum(
  curriculumId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumProgress> {
  const client = ensureClient(options);
  const data = await client.request(COMPLETE_CURRICULUM_MUTATION, {
    curriculumId,
  });
  return (data as any).completeCurriculum;
}

/**
 * 学習パス v2 の項目を済みにする。miniQuizAnswers は読む項目のミニテストで選んだ番号 (問いの順)。その欄の無い項目は null。
 * 済みの条件 (ミニテストの合格など) を満たさなければ、サーバが PATH_V2_COMPLETION_UNMET のエラーで断る
 */
export async function completePathV2Curriculum(
  curriculumId: string,
  miniQuizAnswers: number[] | null,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumProgress> {
  const client = ensureClient(options);
  try {
    const data = await client.request(COMPLETE_PATH_V2_CURRICULUM_MUTATION, {
      curriculumId,
      miniQuizAnswers,
    });
    return (data as any).completeCurriculum;
  } catch (error) {
    // miniQuizAnswers を知らない api-go (client を先に出した間) には、今までの口で送る。
    // その間はサーバが条件を見ないが、画面は条件を満たすまで押させない。api-go を出したあとは通らない
    if (!String((error as { message?: unknown } | null)?.message ?? "").includes('Unknown argument "miniQuizAnswers"')) throw error;
    return completeCurriculum(curriculumId, options);
  }
}

/**
 * カリキュラムのクリアを解除する
 */
export async function uncompleteCurriculum(
  curriculumId: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(UNCOMPLETE_CURRICULUM_MUTATION, {
    curriculumId,
  });
  return (data as any).uncompleteCurriculum;
}

// ========== Team Curriculum Checks (チェックリスト) ==========

const GET_CURRICULUM_CHECKS_QUERY = gql`
  query GetCurriculumChecks($curriculumId: String!) {
    curriculumChecks(curriculumId: $curriculumId) {
      id
      curriculumId
      label
      displayOrder
      createdAt
      isCompleted
    }
  }
`;

const CREATE_CURRICULUM_CHECK_MUTATION = gql`
  mutation CreateCurriculumCheck(
    $curriculumId: String!
    $label: String!
    $displayOrder: Int
  ) {
    createCurriculumCheck(
      curriculumId: $curriculumId
      label: $label
      displayOrder: $displayOrder
    ) {
      id
      curriculumId
      label
      displayOrder
      createdAt
    }
  }
`;

const UPDATE_CURRICULUM_CHECK_MUTATION = gql`
  mutation UpdateCurriculumCheck(
    $id: String!
    $label: String
    $displayOrder: Int
  ) {
    updateCurriculumCheck(id: $id, label: $label, displayOrder: $displayOrder) {
      id
      curriculumId
      label
      displayOrder
      createdAt
    }
  }
`;

const DELETE_CURRICULUM_CHECK_MUTATION = gql`
  mutation DeleteCurriculumCheck($id: String!) {
    deleteCurriculumCheck(id: $id)
  }
`;

const TOGGLE_CHECK_COMPLETION_MUTATION = gql`
  mutation ToggleCheckCompletion($checkId: String!) {
    toggleCheckCompletion(checkId: $checkId)
  }
`;

export interface TeamCurriculumCheck {
  id: string;
  curriculumId: string;
  label: string;
  displayOrder: number;
  createdAt: string;
  isCompleted?: boolean;
}

/**
 * カリキュラムのチェックリストを取得（ユーザーの完了状態付き）
 */
export async function getCurriculumChecks(
  curriculumId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumCheck[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_CURRICULUM_CHECKS_QUERY, {
    curriculumId,
  });
  return (data as any).curriculumChecks ?? [];
}

/**
 * チェック項目を作成（管理者用）
 */
export async function createCurriculumCheck(
  curriculumId: string,
  label: string,
  displayOrder?: number,
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumCheck> {
  const client = ensureClient(options);
  const data = await client.request(CREATE_CURRICULUM_CHECK_MUTATION, {
    curriculumId,
    label,
    displayOrder,
  });
  return (data as any).createCurriculumCheck;
}

/**
 * チェック項目を更新（管理者用）
 */
export async function updateCurriculumCheck(
  id: string,
  data: { label?: string; displayOrder?: number },
  options?: GraphqlTeamOptions,
): Promise<TeamCurriculumCheck> {
  const client = ensureClient(options);
  const result = await client.request(UPDATE_CURRICULUM_CHECK_MUTATION, {
    id,
    ...data,
  });
  return (result as any).updateCurriculumCheck;
}

/**
 * チェック項目を削除（管理者用）
 */
export async function deleteCurriculumCheck(
  id: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_CURRICULUM_CHECK_MUTATION, { id });
  return (data as any).deleteCurriculumCheck;
}

/**
 * チェック完了状態をトグル
 */
export async function toggleCheckCompletion(
  checkId: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(TOGGLE_CHECK_COMPLETION_MUTATION, {
    checkId,
  });
  return (data as any).toggleCheckCompletion;
}

// ========== Curriculum Homeworks (ミニ宿題) ==========

const GET_CURRICULUM_HOMEWORKS_QUERY = gql`
  query GetCurriculumHomeworks($curriculumId: String!) {
    curriculumHomeworks(curriculumId: $curriculumId) {
      id
      curriculumId
      question
      displayOrder
      createdAt
      userAnswer
    }
  }
`;

const CREATE_CURRICULUM_HOMEWORK_MUTATION = gql`
  mutation CreateCurriculumHomework(
    $curriculumId: String!
    $question: String!
    $displayOrder: Int
  ) {
    createCurriculumHomework(
      curriculumId: $curriculumId
      question: $question
      displayOrder: $displayOrder
    ) {
      id
      curriculumId
      question
      displayOrder
      createdAt
    }
  }
`;

const UPDATE_CURRICULUM_HOMEWORK_MUTATION = gql`
  mutation UpdateCurriculumHomework(
    $id: String!
    $question: String
    $displayOrder: Int
  ) {
    updateCurriculumHomework(
      id: $id
      question: $question
      displayOrder: $displayOrder
    ) {
      id
      curriculumId
      question
      displayOrder
      createdAt
    }
  }
`;

const DELETE_CURRICULUM_HOMEWORK_MUTATION = gql`
  mutation DeleteCurriculumHomework($id: String!) {
    deleteCurriculumHomework(id: $id)
  }
`;

const SAVE_HOMEWORK_ANSWER_MUTATION = gql`
  mutation SaveHomeworkAnswer($homeworkId: String!, $answer: String!) {
    saveHomeworkAnswer(homeworkId: $homeworkId, answer: $answer)
  }
`;

export interface CurriculumHomework {
  id: string;
  curriculumId: string;
  question: string;
  displayOrder: number;
  createdAt: string;
  userAnswer?: string | null;
}

/**
 * カリキュラムのミニ宿題を取得（ユーザーの回答付き）
 */
export async function getCurriculumHomeworks(
  curriculumId: string,
  options?: GraphqlTeamOptions,
): Promise<CurriculumHomework[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_CURRICULUM_HOMEWORKS_QUERY, {
    curriculumId,
  });
  return (data as any).curriculumHomeworks ?? [];
}

/**
 * ミニ宿題を作成（管理者用）
 */
export async function createCurriculumHomework(
  curriculumId: string,
  question: string,
  displayOrder?: number,
  options?: GraphqlTeamOptions,
): Promise<CurriculumHomework> {
  const client = ensureClient(options);
  const data = await client.request(CREATE_CURRICULUM_HOMEWORK_MUTATION, {
    curriculumId,
    question,
    displayOrder,
  });
  return (data as any).createCurriculumHomework;
}

/**
 * ミニ宿題を更新（管理者用）
 */
export async function updateCurriculumHomework(
  id: string,
  data: { question?: string; displayOrder?: number },
  options?: GraphqlTeamOptions,
): Promise<CurriculumHomework> {
  const client = ensureClient(options);
  const result = await client.request(UPDATE_CURRICULUM_HOMEWORK_MUTATION, {
    id,
    ...data,
  });
  return (result as any).updateCurriculumHomework;
}

/**
 * ミニ宿題を削除（管理者用）
 */
export async function deleteCurriculumHomework(
  id: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_CURRICULUM_HOMEWORK_MUTATION, {
    id,
  });
  return (data as any).deleteCurriculumHomework;
}

/**
 * ミニ宿題の回答を保存
 */
export async function saveHomeworkAnswer(
  homeworkId: string,
  answer: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(SAVE_HOMEWORK_ANSWER_MUTATION, {
    homeworkId,
    answer,
  });
  return (data as any).saveHomeworkAnswer;
}

// ========== Trial Registration (トライアル登録) ==========

const REGISTER_TRIAL_USER_MUTATION = gql`
  mutation RegisterTrialUser($input: RegisterTrialUserInput!) {
    registerTrialUser(input: $input) {
      id
      teamId
      userId
      role
      trialExpiresAt
      joinedAt
      status
      maskedContactEmail
    }
  }
`;

const GET_MY_TRIAL_ACCESS_QUERY = gql`
  query GetMyTrialAccess($teamId: String!) {
    myTrialAccess(teamId: $teamId) {
      id
      teamId
      userId
      role
      trialExpiresAt
      joinedAt
      status
      maskedContactEmail
    }
  }
`;

export interface RegisterTrialUserInput {
  teamId: string;
  nickname?: string;
  email: string;
  purpose: string;
  source?: string; // 登録元（meta, organic など）
  termsAccepted: boolean;
}

export type TrialStatus = "ACTIVE" | "EXPIRED" | "CONVERTED" | "CANCELLED";

export interface TrialRegistrationResult {
  id: string;
  teamId: string;
  userId: string;
  role: string;
  trialExpiresAt: string;
  joinedAt: string;
  status: TrialStatus;
  maskedContactEmail?: string | null;
}

/**
 * トライアルユーザーを登録
 * - チームに role=trial で追加
 * - trial_expires_at を7日後に設定
 */
export async function registerTrialUser(
  input: RegisterTrialUserInput,
  options?: GraphqlTeamOptions,
): Promise<TrialRegistrationResult> {
  const client = ensureClient(options);
  const data = await client.request(REGISTER_TRIAL_USER_MUTATION, { input });
  return (data as any).registerTrialUser;
}

export async function getMyTrialAccess(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TrialRegistrationResult | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_MY_TRIAL_ACCESS_QUERY, { teamId });
  return (data as any).myTrialAccess ?? null;
}

// ========== 1on1 Memos ==========

const GET_1ON1_MEMOS_QUERY = gql`
  query GetTeam1on1Memos($teamId: String!, $memberId: String) {
    team1on1Memos(teamId: $teamId, memberId: $memberId) {
      id
      teamId
      memberId
      content
      memoDate
      createdBy
      createdAt
      updatedAt
    }
  }
`;

const CREATE_1ON1_MEMO_MUTATION = gql`
  mutation Create1on1Memo(
    $teamId: String!
    $memberId: String!
    $content: String!
    $memoDate: String!
  ) {
    create1on1Memo(
      teamId: $teamId
      memberId: $memberId
      content: $content
      memoDate: $memoDate
    ) {
      id
      teamId
      memberId
      content
      memoDate
      createdBy
      createdAt
      updatedAt
    }
  }
`;

const DELETE_1ON1_MEMO_MUTATION = gql`
  mutation Delete1on1Memo($memoId: String!) {
    delete1on1Memo(memoId: $memoId)
  }
`;

export interface Team1on1Memo {
  id: string;
  teamId: string;
  memberId: string;
  content: string;
  memoDate: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * 1on1メモ一覧を取得
 */
export async function getTeam1on1Memos(
  teamId: string,
  memberId?: string,
  options?: GraphqlTeamOptions,
): Promise<Team1on1Memo[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_1ON1_MEMOS_QUERY, {
    teamId,
    memberId: memberId ?? null,
  });
  return (data as any).team1on1Memos ?? [];
}

/**
 * 1on1メモを作成
 */
export async function create1on1Memo(
  input: {
    teamId: string;
    memberId: string;
    content: string;
    memoDate: string;
  },
  options?: GraphqlTeamOptions,
): Promise<Team1on1Memo> {
  const client = ensureClient(options);
  const data = await client.request(CREATE_1ON1_MEMO_MUTATION, input);
  return (data as any).create1on1Memo;
}

/**
 * 1on1メモを削除
 */
export async function delete1on1Memo(
  memoId: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_1ON1_MEMO_MUTATION, { memoId });
  return (data as any).delete1on1Memo;
}

/** api-go の ErrMemoNotFoundJA (internal/usecase/team/team.go)。拡張の code は付かず、文言がそのまま message に入る */
const MEMO_NOT_FOUND_MESSAGE = "メモが見つかりません";

/**
 * delete1on1Memo の失敗が「そのメモはもう無い」だけかを見る。二度押しや送り直しで
 * 2 回目が来たときは消えた状態がもう出来ているので、呼び出し側は済みとして扱える。
 * 権限・認証・通信の失敗は false にして、そのまま投げ直させる
 */
export function isMemoNotFoundError(error: unknown): boolean {
  const errors = (error as { response?: { errors?: Array<{ message?: unknown }> } } | null)
    ?.response?.errors;
  if (!Array.isArray(errors) || errors.length === 0) return false;
  return errors.every((e) => e?.message === MEMO_NOT_FOUND_MESSAGE);
}

// ========== Referral Codes ==========

const GET_REFERRAL_CODE_QUERY = gql`
  query GetReferralCode($teamId: String!, $userId: String!) {
    referralCode(teamId: $teamId, userId: $userId) {
      id
      teamId
      userId
      code
      totalClicks
      totalLineRegistrations
      totalEnrollments
      totalRewardsSent
      createdAt
    }
  }
`;

const GET_ALL_REFERRAL_CODES_QUERY = gql`
  query GetAllReferralCodes($teamId: String!) {
    referralCodes(teamId: $teamId) {
      id
      teamId
      userId
      code
      totalClicks
      totalLineRegistrations
      totalEnrollments
      totalRewardsSent
      createdAt
      user {
        id
        name
        email
      }
    }
  }
`;

const CREATE_REFERRAL_CODE_MUTATION = gql`
  mutation CreateReferralCode($teamId: String!, $userId: String!) {
    createReferralCode(teamId: $teamId, userId: $userId) {
      id
      teamId
      userId
      code
      totalClicks
      totalLineRegistrations
      totalEnrollments
      totalRewardsSent
      createdAt
    }
  }
`;

export interface ReferralCode {
  id: string;
  teamId: string;
  userId: string;
  code: string;
  totalClicks: number;
  totalLineRegistrations: number;
  totalEnrollments: number;
  totalRewardsSent: number;
  createdAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

/**
 * 紹介コードを取得
 */
export async function getReferralCode(
  teamId: string,
  userId: string,
  options?: GraphqlTeamOptions,
): Promise<ReferralCode | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_REFERRAL_CODE_QUERY, {
    teamId,
    userId,
  });
  return (data as any).referralCode ?? null;
}

/**
 * 全紹介コード取得（管理者用）
 */
export async function getAllReferralCodes(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<ReferralCode[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_ALL_REFERRAL_CODES_QUERY, { teamId });
  return (data as any).referralCodes ?? [];
}

/**
 * 紹介コードを作成
 */
export async function createReferralCode(
  teamId: string,
  userId: string,
  options?: GraphqlTeamOptions,
): Promise<ReferralCode> {
  const client = ensureClient(options);
  const data = await client.request(CREATE_REFERRAL_CODE_MUTATION, {
    teamId,
    userId,
  });
  return (data as any).createReferralCode;
}

// ========== Lecture View Tracking (動画視聴トラッキング) ==========

const GET_MY_LECTURE_VIEW_IDS_QUERY = gql`
  query MyLectureViewIds($teamId: String!) {
    myLectureViewIds(teamId: $teamId)
  }
`;

const MARK_LECTURE_VIEWED_MUTATION = gql`
  mutation MarkLectureViewed($lectureId: String!) {
    markLectureViewed(lectureId: $lectureId)
  }
`;

const UNMARK_LECTURE_VIEWED_MUTATION = gql`
  mutation UnmarkLectureViewed($lectureId: String!) {
    unmarkLectureViewed(lectureId: $lectureId)
  }
`;

/**
 * 自分が視聴済みの講義IDリストを取得
 */
export async function getMyLectureViewIds(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<string[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_MY_LECTURE_VIEW_IDS_QUERY, { teamId });
  return (data as any).myLectureViewIds ?? [];
}

/**
 * 講義を視聴済みとしてマークする
 */
export async function markLectureViewed(
  lectureId: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(MARK_LECTURE_VIEWED_MUTATION, {
    lectureId,
  });
  return (data as any).markLectureViewed;
}

/**
 * 講義の視聴済みマークを解除する
 */
export async function unmarkLectureViewed(
  lectureId: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(UNMARK_LECTURE_VIEWED_MUTATION, {
    lectureId,
  });
  return (data as any).unmarkLectureViewed;
}

const GET_MY_COMPREHENSION_CHECK_SUMMARY_QUERY = gql`
  query MyComprehensionCheckSummary($teamId: String!) {
    myComprehensionCheckSummary(teamId: $teamId) {
      completed
      total
    }
  }
`;

/**
 * 自分の理解度チェック達成状況サマリーを取得する
 */
export async function getMyComprehensionCheckSummary(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<{ completed: number; total: number }> {
  const client = ensureClient(options);
  const data = await client.request(GET_MY_COMPREHENSION_CHECK_SUMMARY_QUERY, {
    teamId,
  });
  return (
    (data as any).myComprehensionCheckSummary ?? { completed: 0, total: 0 }
  );
}

const GET_MY_QUESTION_COUNT_QUERY = gql`
  query MyQuestionCount($teamId: String!) {
    myQuestionCount(teamId: $teamId)
  }
`;

/**
 * 自分の質問数を取得する（Mattermost 集計）
 */
export async function getMyQuestionCount(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<number> {
  const client = ensureClient(options);
  try {
    const data = await client.request(GET_MY_QUESTION_COUNT_QUERY, { teamId });
    return (data as any).myQuestionCount ?? 0;
  } catch {
    return 0;
  }
}

const GET_MY_PRACTICAL_PROJECT_PROGRESS_QUERY = gql`
  query MyPracticalProjectProgress {
    myPracticalProjectProgress {
      projectId
      chapterIndex
      status
    }
  }
`;

const MARK_CHAPTER_COMPLETED_MUTATION = gql`
  mutation MarkChapterCompleted($projectId: String!, $chapterIndex: Int!) {
    markChapterCompleted(projectId: $projectId, chapterIndex: $chapterIndex)
  }
`;

const UNMARK_CHAPTER_COMPLETED_MUTATION = gql`
  mutation UnmarkChapterCompleted($projectId: String!, $chapterIndex: Int!) {
    unmarkChapterCompleted(projectId: $projectId, chapterIndex: $chapterIndex)
  }
`;

/**
 * 自分の実践プロジェクトの章進捗を取得する
 */
export async function getMyPracticalProjectProgress(
  options?: GraphqlTeamOptions,
): Promise<{ projectId: string; chapterIndex: number; status: string }[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_MY_PRACTICAL_PROJECT_PROGRESS_QUERY);
  return (data as any).myPracticalProjectProgress ?? [];
}

/**
 * 実践プロジェクトの章を完了済みとしてマークする
 */
export async function markChapterCompleted(
  projectId: string,
  chapterIndex: number,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(MARK_CHAPTER_COMPLETED_MUTATION, {
    projectId,
    chapterIndex,
  });
  return (data as any).markChapterCompleted;
}

/**
 * 実践プロジェクトの章の完了済みマークを解除する
 */
export async function unmarkChapterCompleted(
  projectId: string,
  chapterIndex: number,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(UNMARK_CHAPTER_COMPLETED_MUTATION, {
    projectId,
    chapterIndex,
  });
  return (data as any).unmarkChapterCompleted;
}

// ========== Monthly Reports (LAYER 8) ==========

export interface TeamMonthlyReport {
  id: string;
  userId: string;
  teamId: string;
  year: number;
  month: number;
  studyHours: number;
  score: number;
  summary: string;
  nextGoal: string;
  concern: string | null;
  createdAt: string;
  updatedAt: string;
}

const GET_MY_MONTHLY_REPORTS_QUERY = gql`
  query MyMonthlyReports($teamId: String!) {
    myMonthlyReports(teamId: $teamId) {
      id
      userId
      teamId
      year
      month
      studyHours
      score
      summary
      nextGoal
      concern
      createdAt
      updatedAt
    }
  }
`;

const GET_MY_MONTHLY_REPORT_COUNT_QUERY = gql`
  query MyMonthlyReportCount($teamId: String!) {
    myMonthlyReportCount(teamId: $teamId)
  }
`;

const GET_MEMBER_MONTHLY_REPORTS_QUERY = gql`
  query MemberMonthlyReports($teamId: String!, $userId: String!) {
    memberMonthlyReports(teamId: $teamId, userId: $userId) {
      id
      userId
      teamId
      year
      month
      studyHours
      score
      summary
      nextGoal
      concern
      createdAt
      updatedAt
    }
  }
`;

const SUBMIT_MONTHLY_REPORT_MUTATION = gql`
  mutation SubmitMonthlyReport(
    $teamId: String!
    $year: Int!
    $month: Int!
    $studyHours: Int!
    $score: Int!
    $summary: String!
    $nextGoal: String!
    $concern: String
  ) {
    submitMonthlyReport(
      teamId: $teamId
      year: $year
      month: $month
      studyHours: $studyHours
      score: $score
      summary: $summary
      nextGoal: $nextGoal
      concern: $concern
    ) {
      id
      userId
      teamId
      year
      month
      studyHours
      score
      summary
      nextGoal
      concern
      createdAt
      updatedAt
    }
  }
`;

/**
 * 自分が提出した月報の一覧を取得する（新しい順）
 */
export async function getMyMonthlyReports(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamMonthlyReport[]> {
  const client = ensureClient(options);
  try {
    const data = await client.request(GET_MY_MONTHLY_REPORTS_QUERY, { teamId });
    return (data as any).myMonthlyReports ?? [];
  } catch {
    return [];
  }
}

/**
 * 指定した受講生の月報一覧を取得する（担当メンター / 管理者のみ。1on1 面談用）
 */
export async function getMemberMonthlyReports(
  teamId: string,
  userId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamMonthlyReport[]> {
  const client = ensureClient(options);
  try {
    const data = await client.request(GET_MEMBER_MONTHLY_REPORTS_QUERY, {
      teamId,
      userId,
    });
    return (data as any).memberMonthlyReports ?? [];
  } catch {
    return [];
  }
}

/**
 * 自分が提出した月報の件数を取得する（LAYER 8 用）
 */
export async function getMyMonthlyReportCount(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<number> {
  const client = ensureClient(options);
  try {
    const data = await client.request(GET_MY_MONTHLY_REPORT_COUNT_QUERY, {
      teamId,
    });
    return (data as any).myMonthlyReportCount ?? 0;
  } catch {
    return 0;
  }
}

/**
 * 月報を提出する（同月分があれば更新する UPSERT）
 */
export async function submitMonthlyReport(
  teamId: string,
  year: number,
  month: number,
  studyHours: number,
  score: number,
  summary: string,
  nextGoal: string,
  concern: string | null | undefined,
  options?: GraphqlTeamOptions,
): Promise<TeamMonthlyReport> {
  const client = ensureClient(options);
  const data = await client.request(SUBMIT_MONTHLY_REPORT_MUTATION, {
    teamId,
    year,
    month,
    studyHours,
    score,
    summary,
    nextGoal,
    concern: concern ?? null,
  });
  return (data as any).submitMonthlyReport;
}

// ========== Monthly Report Ranking ==========

export interface MonthlyReportRankingEntry {
  rank: number;
  userId: string;
  userName: string;
  userPhotoUrl: string | null;
  studyHours: number;
  score: number;
  questionCount: number;
  mvpScore: number;
}

export interface MonthlyReportRanking {
  year: number;
  month: number;
  totalSubmissions: number;
  mvp: MonthlyReportRankingEntry | null;
  studyHoursTop: MonthlyReportRankingEntry[];
  questionTop: MonthlyReportRankingEntry[];
  myEntry: MonthlyReportRankingEntry | null;
  myStudyHoursRank: number | null;
  myQuestionRank: number | null;
  isAdmin: boolean;
}

const RANKING_ENTRY_FIELDS = `
  rank
  userId
  userName
  userPhotoUrl
  studyHours
  score
  questionCount
  mvpScore
`;

const GET_MONTHLY_REPORT_RANKING_QUERY = gql`
  query MonthlyReportRanking($teamId: String!, $year: Int!, $month: Int!) {
    monthlyReportRanking(teamId: $teamId, year: $year, month: $month) {
      year
      month
      totalSubmissions
      isAdmin
      myStudyHoursRank
      myQuestionRank
      mvp {
        ${RANKING_ENTRY_FIELDS}
      }
      studyHoursTop {
        ${RANKING_ENTRY_FIELDS}
      }
      questionTop {
        ${RANKING_ENTRY_FIELDS}
      }
      myEntry {
        ${RANKING_ENTRY_FIELDS}
      }
    }
  }
`;

const GET_MONTHLY_REPORT_AVAILABLE_MONTHS_QUERY = gql`
  query MonthlyReportAvailableMonths($teamId: String!) {
    monthlyReportAvailableMonths(teamId: $teamId) {
      year
      month
    }
  }
`;

/**
 * 月報ランキング（指定 year/month）を取得
 */
export async function getMonthlyReportRanking(
  teamId: string,
  year: number,
  month: number,
  options?: GraphqlTeamOptions,
): Promise<MonthlyReportRanking | null> {
  const client = ensureClient(options);
  try {
    const data = await client.request(GET_MONTHLY_REPORT_RANKING_QUERY, {
      teamId,
      year,
      month,
    });
    return (data as any).monthlyReportRanking ?? null;
  } catch (err) {
    console.error("getMonthlyReportRanking error:", err);
    return null;
  }
}

/**
 * 過去12ヶ月のうち、月報提出がある月リスト（月選択UI用）
 */
export async function getMonthlyReportAvailableMonths(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<{ year: number; month: number }[]> {
  const client = ensureClient(options);
  try {
    const data = await client.request(
      GET_MONTHLY_REPORT_AVAILABLE_MONTHS_QUERY,
      { teamId },
    );
    return (data as any).monthlyReportAvailableMonths ?? [];
  } catch (err) {
    console.error("getMonthlyReportAvailableMonths error:", err);
    return [];
  }
}

// ========== Member Goal Sheets ==========

export interface TeamMemberGoalSheet {
  id: string;
  userId: string;
  teamId: string;
  birthdate: string | null;
  currentState: string | null;
  gapActions: string | null;
  goal18m: string;
  goal6m: string;
  goal3y: string | null;
  constraints: string | null;
  messageToMentor: string | null;
  mentorComment: string | null;
  mentorCommentAt: string | null;
  mentorCommentBy: string | null;
  createdAt: string;
  updatedAt: string;
}

const GOAL_SHEET_FIELDS = `
  id
  userId
  teamId
  birthdate
  currentState
  gapActions
  goal18m
  goal6m
  goal3y
  constraints
  messageToMentor
  mentorComment
  mentorCommentAt
  mentorCommentBy
  createdAt
  updatedAt
`;

const GET_MY_GOAL_SHEET_QUERY = gql`
  query MyGoalSheet($teamId: String!) {
    myGoalSheet(teamId: $teamId) {
      ${GOAL_SHEET_FIELDS}
    }
  }
`;

const GET_MEMBER_GOAL_SHEET_QUERY = gql`
  query MemberGoalSheet($teamId: String!, $userId: String!) {
    memberGoalSheet(teamId: $teamId, userId: $userId) {
      ${GOAL_SHEET_FIELDS}
    }
  }
`;

const SUBMIT_GOAL_SHEET_MUTATION = gql`
  mutation SubmitGoalSheet(
    $teamId: String!
    $birthdate: String
    $currentState: String!
    $gapActions: String
    $goal18m: String!
    $goal6m: String!
    $goal3y: String
    $constraints: String
    $messageToMentor: String
  ) {
    submitGoalSheet(
      teamId: $teamId
      birthdate: $birthdate
      currentState: $currentState
      gapActions: $gapActions
      goal18m: $goal18m
      goal6m: $goal6m
      goal3y: $goal3y
      constraints: $constraints
      messageToMentor: $messageToMentor
    ) {
      ${GOAL_SHEET_FIELDS}
    }
  }
`;

const ADD_MENTOR_GOAL_COMMENT_MUTATION = gql`
  mutation AddMentorGoalComment(
    $teamId: String!
    $userId: String!
    $comment: String!
  ) {
    addMentorGoalComment(teamId: $teamId, userId: $userId, comment: $comment) {
      ${GOAL_SHEET_FIELDS}
    }
  }
`;

/**
 * 自分の目標シートを取得（無ければ null）
 */
export async function getMyGoalSheet(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamMemberGoalSheet | null> {
  const client = ensureClient(options);
  try {
    const data = await client.request(GET_MY_GOAL_SHEET_QUERY, { teamId });
    return (data as any).myGoalSheet ?? null;
  } catch (err) {
    console.error("getMyGoalSheet error:", err);
    return null;
  }
}

/**
 * 指定メンバーの目標シートを取得（担当メンター or admin/owner のみ）
 */
export async function getMemberGoalSheet(
  teamId: string,
  userId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamMemberGoalSheet | null> {
  const client = ensureClient(options);
  try {
    const data = await client.request(GET_MEMBER_GOAL_SHEET_QUERY, {
      teamId,
      userId,
    });
    return (data as any).memberGoalSheet ?? null;
  } catch (err) {
    console.error("getMemberGoalSheet error:", err);
    return null;
  }
}

/**
 * 自分の目標シートを保存（UPSERT）
 */
export async function submitGoalSheet(
  teamId: string,
  birthdate: string | null | undefined,
  currentState: string,
  gapActions: string | null | undefined,
  goal18m: string,
  goal6m: string,
  goal3y: string | null | undefined,
  constraints: string | null | undefined,
  messageToMentor: string | null | undefined,
  options?: GraphqlTeamOptions,
): Promise<TeamMemberGoalSheet> {
  const client = ensureClient(options);
  const data = await client.request(SUBMIT_GOAL_SHEET_MUTATION, {
    teamId,
    birthdate: birthdate ?? null,
    currentState,
    gapActions: gapActions ?? null,
    goal18m,
    goal6m,
    goal3y: goal3y ?? null,
    constraints: constraints ?? null,
    messageToMentor: messageToMentor ?? null,
  });
  return (data as any).submitGoalSheet;
}

/**
 * 担当メンター/admin/owner からのコメントを追加
 */
export async function addMentorGoalComment(
  teamId: string,
  userId: string,
  comment: string,
  options?: GraphqlTeamOptions,
): Promise<TeamMemberGoalSheet> {
  const client = ensureClient(options);
  const data = await client.request(ADD_MENTOR_GOAL_COMMENT_MUTATION, {
    teamId,
    userId,
    comment,
  });
  return (data as any).addMentorGoalComment;
}

// ========== Meeting Logs ==========

export interface TeamMeetingLog {
  id: string;
  userId: string;
  teamId: string;
  meetingDate: string; // YYYY-MM-DD
  notes: string;
  nextActions: string | null;
  writtenBy: string;
  writtenByName: string;
  createdAt: string;
  updatedAt: string;
}

const MEETING_LOG_FIELDS = `
  id
  userId
  teamId
  meetingDate
  notes
  nextActions
  writtenBy
  writtenByName
  createdAt
  updatedAt
`;

const GET_MY_MEETING_LOGS_QUERY = gql`
  query MyMeetingLogs($teamId: String!) {
    myMeetingLogs(teamId: $teamId) {
      ${MEETING_LOG_FIELDS}
    }
  }
`;

const GET_MEMBER_MEETING_LOGS_QUERY = gql`
  query MemberMeetingLogs($teamId: String!, $userId: String!) {
    memberMeetingLogs(teamId: $teamId, userId: $userId) {
      ${MEETING_LOG_FIELDS}
    }
  }
`;

const ADD_MEETING_LOG_MUTATION = gql`
  mutation AddMeetingLog(
    $teamId: String!
    $userId: String!
    $meetingDate: String!
    $notes: String!
    $nextActions: String
  ) {
    addMeetingLog(
      teamId: $teamId
      userId: $userId
      meetingDate: $meetingDate
      notes: $notes
      nextActions: $nextActions
    ) {
      ${MEETING_LOG_FIELDS}
    }
  }
`;

const UPDATE_MEETING_LOG_MUTATION = gql`
  mutation UpdateMeetingLog(
    $id: String!
    $meetingDate: String!
    $notes: String!
    $nextActions: String
  ) {
    updateMeetingLog(
      id: $id
      meetingDate: $meetingDate
      notes: $notes
      nextActions: $nextActions
    ) {
      ${MEETING_LOG_FIELDS}
    }
  }
`;

const DELETE_MEETING_LOG_MUTATION = gql`
  mutation DeleteMeetingLog($id: String!) {
    deleteMeetingLog(id: $id)
  }
`;

/**
 * 自分の面談ログを一覧取得（meeting_date 降順）
 */
export async function getMyMeetingLogs(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamMeetingLog[]> {
  const client = ensureClient(options);
  try {
    const data = await client.request(GET_MY_MEETING_LOGS_QUERY, { teamId });
    return (data as any).myMeetingLogs ?? [];
  } catch (err) {
    console.error("getMyMeetingLogs error:", err);
    return [];
  }
}

/**
 * 指定メンバーの面談ログを一覧取得（自身/担当メンター/admin/owner のみ）
 */
export async function getMemberMeetingLogs(
  teamId: string,
  userId: string,
  options?: GraphqlTeamOptions,
): Promise<TeamMeetingLog[]> {
  const client = ensureClient(options);
  try {
    const data = await client.request(GET_MEMBER_MEETING_LOGS_QUERY, {
      teamId,
      userId,
    });
    return (data as any).memberMeetingLogs ?? [];
  } catch (err) {
    console.error("getMemberMeetingLogs error:", err);
    return [];
  }
}

/**
 * 面談ログを追加
 */
export async function addMeetingLog(
  teamId: string,
  userId: string,
  meetingDate: string,
  notes: string,
  nextActions: string | null | undefined,
  options?: GraphqlTeamOptions,
): Promise<TeamMeetingLog> {
  const client = ensureClient(options);
  const data = await client.request(ADD_MEETING_LOG_MUTATION, {
    teamId,
    userId,
    meetingDate,
    notes,
    nextActions: nextActions ?? null,
  });
  return (data as any).addMeetingLog;
}

/**
 * 面談ログを更新（自分が書いたもの or admin/owner のみ）
 */
export async function updateMeetingLog(
  id: string,
  meetingDate: string,
  notes: string,
  nextActions: string | null | undefined,
  options?: GraphqlTeamOptions,
): Promise<TeamMeetingLog> {
  const client = ensureClient(options);
  const data = await client.request(UPDATE_MEETING_LOG_MUTATION, {
    id,
    meetingDate,
    notes,
    nextActions: nextActions ?? null,
  });
  return (data as any).updateMeetingLog;
}

/**
 * 面談ログを削除（自分が書いたもの or admin/owner のみ）
 */
export async function deleteMeetingLog(
  id: string,
  options?: GraphqlTeamOptions,
): Promise<boolean> {
  const client = ensureClient(options);
  const data = await client.request(DELETE_MEETING_LOG_MUTATION, { id });
  return Boolean((data as any).deleteMeetingLog);
}

// ========== Curriculum Quiz (確認テスト・理解度クイズ・beta) ==========
// team_curriculum_checks（バイナリ式チェックリスト）とは別概念。採点式の4択クイズ。

export interface ConfirmationTestCurriculum {
  id: string;
  lpCourseId?: string | null;
  title: string;
  itemType: string;
  displayOrder: number;
}

export interface ConfirmationTestCourse {
  id: string;
  title: string;
  displayOrder: number;
}

export interface CurriculumQuizPublicQuestion {
  id: string;
  question: string;
  choices: string[];
  difficulty: "beginner" | "intermediate" | "advanced";
}

export interface ConfirmationTestCatalogItem {
  curriculum: ConfirmationTestCurriculum;
  overview: CurriculumQuizOverviewItem;
}

export interface ConfirmationTestCatalog {
  items: ConfirmationTestCatalogItem[];
  courses: ConfirmationTestCourse[];
}

export interface ConfirmationTestDetail {
  curriculum: ConfirmationTestCurriculum;
  questions: CurriculumQuizPublicQuestion[];
  catalog: ConfirmationTestCatalog;
}

export const GET_CONFIRMATION_TEST_CATALOG_QUERY = gql`
  query GetConfirmationTestCatalog($teamId: String!) {
    confirmationTestCatalog(teamId: $teamId) {
      items {
        curriculum {
          id
          lpCourseId
          title
          itemType
          displayOrder
        }
        overview {
          curriculumId
          questionCount
          bestAttempt {
            score
            total
            isPassed
            passThreshold
          }
        }
      }
      courses {
        id
        title
        displayOrder
      }
    }
  }
`;

export const GET_CONFIRMATION_TEST_DETAIL_QUERY = gql`
  query GetConfirmationTestDetail($curriculumId: String!) {
    confirmationTestDetail(curriculumId: $curriculumId) {
      curriculum {
        id
        lpCourseId
        title
        itemType
        displayOrder
      }
      questions {
        id
        question
        choices
        difficulty
      }
      catalog {
        items {
          curriculum {
            id
            lpCourseId
            title
            itemType
            displayOrder
          }
          overview {
            curriculumId
            questionCount
            bestAttempt {
              score
              total
              isPassed
              passThreshold
            }
          }
        }
        courses {
          id
          title
          displayOrder
        }
      }
    }
  }
`;

const EMPTY_CONFIRMATION_TEST_CATALOG: ConfirmationTestCatalog = {
  items: [],
  courses: [],
};

/** One GraphQL request for the complete confirmation-test list. */
export async function getConfirmationTestCatalog(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<ConfirmationTestCatalog> {
  const client = ensureClient(options);
  const data = await client.request(GET_CONFIRMATION_TEST_CATALOG_QUERY, {
    teamId,
  });
  return (
    (data as any).confirmationTestCatalog ?? EMPTY_CONFIRMATION_TEST_CATALOG
  );
}

/**
 * One GraphQL request for the required heading/questions and fail-open sidebar.
 * The public question type has no answer-key or explanation fields.
 */
export async function getConfirmationTestDetail(
  curriculumId: string,
  options?: GraphqlTeamOptions,
): Promise<ConfirmationTestDetail | null> {
  const client = ensureClient(options);
  const data = await client.request(GET_CONFIRMATION_TEST_DETAIL_QUERY, {
    curriculumId,
  });
  return (data as any).confirmationTestDetail ?? null;
}

const GET_CURRICULUM_QUIZ_QUESTIONS_QUERY = gql`
  query GetCurriculumQuizQuestions($curriculumId: String!) {
    curriculumQuizQuestions(curriculumId: $curriculumId) {
      id
      question
      choices
      difficulty
    }
  }
`;

const SUBMIT_CURRICULUM_QUIZ_MUTATION = gql`
  mutation SubmitCurriculumQuiz(
    $curriculumId: String!
    $answers: [CurriculumQuizAnswerInput!]!
  ) {
    submitCurriculumQuiz(curriculumId: $curriculumId, answers: $answers) {
      attempt {
        score
        total
        isPassed
        passThreshold
      }
      results {
        questionId
        isCorrect
        correctIndex
        explanation
      }
    }
  }
`;

const GET_CURRICULUM_QUIZ_OVERVIEW_QUERY = gql`
  query GetCurriculumQuizOverview($teamId: String!) {
    curriculumQuizOverview(teamId: $teamId) {
      curriculumId
      questionCount
      bestAttempt {
        score
        total
        isPassed
        passThreshold
      }
    }
  }
`;

/**
 * 確認テストの出題。受験前は正解 (correctIndex) と解説 (explanation) を含まない。
 */
export interface CurriculumQuizQuestion {
  id: string;
  question: string;
  choices: string[];
  correctIndex: number;
  difficulty: "beginner" | "intermediate" | "advanced";
  explanation?: string | null;
}

export interface CurriculumQuizAttemptResult {
  score: number;
  total: number;
  isPassed: boolean;
  passThreshold: number;
}

export interface CurriculumQuizAnswerResult {
  questionId: string;
  isCorrect: boolean;
  correctIndex: number;
  explanation?: string | null;
}

export interface CurriculumQuizSubmitResult {
  attempt: CurriculumQuizAttemptResult;
  results: CurriculumQuizAnswerResult[];
}

export interface CurriculumQuizOverviewItem {
  curriculumId: string;
  questionCount: number;
  // bestAttempt という名称だが、実体は最新の受験結果（最高得点ではない）
  bestAttempt: CurriculumQuizAttemptResult | null;
}

/**
 * カリキュラムの確認テスト出題一覧を取得（受験前・正解や解説は含まない）。
 * curriculum が存在しない場合は例外を投げる。
 */
export async function getCurriculumQuizQuestions(
  curriculumId: string,
  options?: GraphqlTeamOptions,
): Promise<CurriculumQuizQuestion[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_CURRICULUM_QUIZ_QUESTIONS_QUERY, {
    curriculumId,
  });
  const questions = (data as any).curriculumQuizQuestions;
  if (!questions) {
    throw new Error(
      `Curriculum quiz questions not found for curriculumId: ${curriculumId}`,
    );
  }
  return questions;
}

/**
 * 確認テストの解答を提出し、採点結果を取得する。
 */
export async function submitCurriculumQuiz(
  curriculumId: string,
  answers: { questionId: string; selectedIndex: number }[],
  options?: GraphqlTeamOptions,
): Promise<CurriculumQuizSubmitResult> {
  const client = ensureClient(options);
  const data = await client.request(SUBMIT_CURRICULUM_QUIZ_MUTATION, {
    curriculumId,
    answers,
  });
  return (data as any).submitCurriculumQuiz;
}

/**
 * チーム内の確認テスト一覧用サマリ（出題数・自分の最新受験結果）を取得する。
 */
export async function getCurriculumQuizOverview(
  teamId: string,
  options?: GraphqlTeamOptions,
): Promise<CurriculumQuizOverviewItem[]> {
  const client = ensureClient(options);
  const data = await client.request(GET_CURRICULUM_QUIZ_OVERVIEW_QUERY, {
    teamId,
  });
  return (data as any).curriculumQuizOverview ?? [];
}

export interface DailyReportPost {
  id: string;
  authorName: string;
  authorPhotoUrl: string | null;
  message: string;
  createdAt: string;
  replyCount: number;
}

const GET_RECENT_DAILY_REPORTS_QUERY = gql`
  query RecentDailyReports($limit: Int) {
    recentDailyReports(limit: $limit) {
      id
      authorName
      authorPhotoUrl
      message
      createdAt
      replyCount
    }
  }
`;

/**
 * Mattermost「#日報・学習記録」チャンネルの最新投稿一覧を取得する。
 */
export async function getRecentDailyReports(
  limit?: number,
  options?: GraphqlTeamOptions,
): Promise<DailyReportPost[]> {
  const client = ensureClient(options);
  try {
    const data = await client.request(GET_RECENT_DAILY_REPORTS_QUERY, {
      limit: limit ?? null,
    });
    return (data as any).recentDailyReports ?? [];
  } catch (err) {
    console.error("getRecentDailyReports error:", err);
    return [];
  }
}
